import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { 
  BookOpen, TrendingDown, Clock, Zap, Wifi, Shield, CreditCard, Car, Plane, 
  ShoppingCart, Tv, Gift, Phone, ExternalLink, CheckCircle, AlertTriangle, 
  Users, Home, Eye, Star, Plane as PlaneIcon, ChevronDown, ChevronUp, Menu, X
} from 'lucide-react';
import { getSaveaPhone, getSaveaPhoneTel } from '../config/phoneConfig';

export const AntiInflationGuidePage = () => {
  const saveaPhone = getSaveaPhone('FR', 'electricity', 'LP');
  const [activeChapter, setActiveChapter] = useState('');
  const [showTableOfContents, setShowTableOfContents] = useState(false);
  const [isMobileTOCOpen, setIsMobileTOCOpen] = useState(false);

  // Chapitres du guide
  const chapters = [
    { id: 'chapitre-1', title: 'Ch.1 - Énergie', icon: '⚡', savings: '30-60€/mois' },
    { id: 'chapitre-2', title: 'Ch.2 - Internet & Télécom', icon: '🌐', savings: '20-40€/mois' },
    { id: 'chapitre-3', title: 'Ch.3 - Assurances', icon: '🛡️', savings: '30-60€/mois' },
    { id: 'chapitre-4', title: 'Ch.4 - Courses & Alimentation', icon: '🛒', savings: '100-200€/mois' },
    { id: 'chapitre-5', title: 'Ch.5 - Banque & Crédits', icon: '💳', savings: '20-50€/mois' },
    { id: 'chapitre-6', title: 'Ch.6 - Transport & Mobilité', icon: '🚗', savings: '50-100€/mois' },
    { id: 'chapitre-7', title: 'Ch.7 - Voyages & Loisirs', icon: '✈️', savings: '50-150€/mois' },
    { id: 'chapitre-8', title: 'Ch.8 - Shopping & Vêtements', icon: '👕', savings: '30-80€/mois' },
    { id: 'chapitre-9', title: 'Ch.9 - Streaming & Abonnements', icon: '📺', savings: '20-50€/mois' },
  ];

  // Détecter le scroll pour afficher/masquer le sommaire et mettre à jour le chapitre actif
  useEffect(() => {
    const handleScroll = () => {
      // Afficher le sommaire après 300px de scroll
      setShowTableOfContents(window.scrollY > 300);

      // Détecter le chapitre actif
      const chapterElements = chapters.map(ch => ({
        id: ch.id,
        element: document.getElementById(ch.id)
      }));

      for (let i = chapterElements.length - 1; i >= 0; i--) {
        const el = chapterElements[i].element;
        if (el && el.getBoundingClientRect().top <= 150) {
          setActiveChapter(chapterElements[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Fonction de navigation vers un chapitre
  const scrollToChapter = (chapterId) => {
    const element = document.getElementById(chapterId);
    if (element) {
      const offset = 80; // Offset pour le header
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;

      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
      setIsMobileTOCOpen(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-sage-50 to-white relative">
      {/* Sommaire Fixe Desktop */}
      {showTableOfContents && (
        <div className="hidden lg:block fixed left-4 top-24 w-64 bg-white rounded-xl shadow-lg border border-sage-200 z-40 max-h-[calc(100vh-120px)] overflow-y-auto">
          <div className="p-4 border-b border-sage-200 bg-gradient-to-r from-amber-500 to-yellow-500">
            <h3 className="font-bold text-white flex items-center gap-2">
              <BookOpen className="w-5 h-5" />
              Sommaire
            </h3>
          </div>
          <nav className="p-2">
            {chapters.map((chapter) => (
              <button
                key={chapter.id}
                onClick={() => scrollToChapter(chapter.id)}
                className={`w-full text-left px-3 py-2.5 rounded-lg transition-all mb-1 ${
                  activeChapter === chapter.id
                    ? 'bg-amber-100 text-amber-900 font-semibold'
                    : 'hover:bg-sage-50 text-sage-700'
                }`}
              >
                <div className="flex items-start gap-2">
                  <span className="text-lg flex-shrink-0">{chapter.icon}</span>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium truncate">{chapter.title}</div>
                    <div className="text-xs text-sage-500 mt-0.5">{chapter.savings}</div>
                  </div>
                </div>
              </button>
            ))}
            <Link
              to="/fr/produits-testes"
              className="w-full text-left px-3 py-2.5 rounded-lg transition-all mb-1 hover:bg-amber-50 text-amber-700 font-medium border-t border-amber-200 mt-2 pt-3 block"
            >
              <div className="flex items-center gap-2">
                <span className="text-lg">🛒</span>
                <span className="text-sm">Produits Testés</span>
              </div>
            </Link>
          </nav>
        </div>
      )}

      {/* Bouton Sommaire Mobile */}
      <button
        onClick={() => setIsMobileTOCOpen(!isMobileTOCOpen)}
        className={`lg:hidden fixed right-4 bg-gradient-to-r from-amber-500 to-yellow-500 text-white rounded-full shadow-lg z-50 p-3 transition-all ${
          showTableOfContents ? 'bottom-20' : 'bottom-4'
        }`}
      >
        {isMobileTOCOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
      </button>

      {/* Sommaire Mobile (Modal) */}
      {isMobileTOCOpen && (
        <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setIsMobileTOCOpen(false)}>
          <div
            className="absolute bottom-0 left-0 right-0 bg-white rounded-t-2xl shadow-2xl max-h-[80vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="sticky top-0 p-4 border-b border-sage-200 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-t-2xl">
              <h3 className="font-bold text-white flex items-center gap-2">
                <BookOpen className="w-5 h-5" />
                Sommaire du Guide
              </h3>
            </div>
            <nav className="p-4">
              {chapters.map((chapter) => (
                <button
                  key={chapter.id}
                  onClick={() => scrollToChapter(chapter.id)}
                  className={`w-full text-left px-4 py-3 rounded-lg transition-all mb-2 ${
                    activeChapter === chapter.id
                      ? 'bg-amber-100 text-amber-900 font-semibold'
                      : 'bg-sage-50 hover:bg-sage-100 text-sage-700'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl flex-shrink-0">{chapter.icon}</span>
                    <div className="flex-1">
                      <div className="font-medium">{chapter.title}</div>
                      <div className="text-sm text-sage-600 mt-1">Économie: {chapter.savings}</div>
                    </div>
                  </div>
                </button>
              ))}
              <Link
                to="/fr/produits-testes"
                className="w-full text-left px-4 py-3 rounded-lg transition-all bg-amber-50 hover:bg-amber-100 text-amber-700 font-medium border-t-2 border-amber-200 mt-4 pt-4 block"
              >
                <div className="flex items-center gap-3">
                  <span className="text-2xl">🛒</span>
                  <span className="font-medium">Voir les Produits Testés SAVEA</span>
                </div>
              </Link>
            </nav>
          </div>
        </div>
      )}

      {/* Hero Section avec image d'illustration */}
      <section className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <div className="flex items-center gap-2 mb-4">
                <Badge className="bg-green-100 text-green-800 border-green-200 px-3 py-1">
                  <Gift className="w-4 h-4 mr-1" />
                  GRATUIT
                </Badge>
                <Badge className="bg-blue-100 text-blue-800 border-blue-200 px-3 py-1">
                  Nouveau 2026
                </Badge>
              </div>
              
              <h1 className="text-4xl lg:text-5xl font-bold text-sage-900 mb-6">
                🏆 Guide Anti-Inflation 2026
              </h1>
              
              <p className="text-xl text-sage-600 mb-6 leading-relaxed">
                Comment économiser entre <strong className="text-green-600">150 € et 300 € par mois</strong> pour votre foyer, 
                sans vous priver ? Découvrez les conseils SAVEA testés et validés.
              </p>

              <div className="bg-green-50 border border-green-200 rounded-lg p-6 mb-8">
                <h3 className="font-bold text-green-800 mb-3">✨ Information SAVEA :</h3>
                <p className="text-green-700">
                  Depuis trop longtemps, nous subissons l'inflation et les hausses imposées par les grandes entreprises. 
                  En réalité, il existe plein de solutions simples, légales et efficaces pour réduire vos factures sans changer votre confort. 
                  Chaque conseil a été testé et validé par nos équipes SAVEA.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 lg:gap-4 mb-8">
                <Button 
                  onClick={() => scrollToChapter('chapitre-1')}
                  size="lg"
                  className="bg-gradient-to-r from-sage-600 to-sage-700 text-white px-4 py-3 lg:px-8 lg:py-4 text-sm lg:text-base"
                >
                  <BookOpen className="w-4 h-4 lg:w-5 lg:h-5 mr-2" />
                  Consulter le Guide
                </Button>
                
                <Button 
                  onClick={() => window.open(`tel:${getSaveaPhoneTel('FR', 'electricity', 'LP')}`)}
                  variant="outline"
                  size="lg"
                  className="border-2 border-sage-600 text-sage-600 hover:bg-sage-600 hover:text-white px-4 py-3 lg:px-8 lg:py-4 text-sm lg:text-base"
                >
                  <Phone className="w-4 h-4 lg:w-5 lg:h-5 mr-2" />
                  <span className="hidden sm:inline">Parler à un Expert</span>
                  <span className="sm:hidden">Expert SAVEA</span>
                </Button>
              </div>

              <div className="flex items-center gap-6 text-sm text-sage-600 mb-8">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>10 chapitres détaillés</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>Conseils testés SAVEA</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <span>100% gratuit</span>
                </div>
              </div>

              {/* Disclaimer légal */}
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-1" />
                  <div>
                    <h4 className="font-semibold text-yellow-800 mb-2">⚠️ Avertissement Important</h4>
                    <div className="text-yellow-700 text-sm space-y-2 leading-relaxed">
                      <p><strong>Les économies affichées sont à titre indicatif et ne sont absolument pas garanties.</strong> En utilisant ce guide, le lecteur reconnaît que :</p>
                      <ul className="list-disc pl-4 space-y-1 text-xs">
                        <li>le contenu est fourni à titre informatif uniquement, sans garantie de complétude</li>
                        <li>SAVEA ne fournit aucun conseil personnalisé et n'intervient pas dans les décisions finales</li>
                        <li>toute mise en œuvre repose sur la responsabilité exclusive du lecteur</li>
                      </ul>
                      <p className="text-xs mt-2"><strong>SAVEA décline toute responsabilité</strong> concernant les erreurs, conséquences financières ou contenus externes.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Image d'illustration */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-2xl">
                <img 
                  src="/images/local/anti-inflation-guide.png"
                  alt="Guide Anti-Inflation 2026 SAVEA"
                  className="w-full h-auto object-cover"
                />
              </div>
              <div className="absolute -top-4 -right-4 bg-green-500 text-white rounded-2xl p-4 shadow-lg">
                <div className="text-2xl font-bold">1500-3000€</div>
                <div className="text-xs">économisés/an</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction importante */}
      <section className="py-12 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="border-blue-200">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-blue-800">
                <AlertTriangle className="w-5 h-5" />
                💡 Information importante avant de commencer
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-blue-700 leading-relaxed">
                Dans ce guide, nous recommandons parfois des produits disponibles sur Amazon. En toute transparence : 
                si vous passez par ces liens, SAVEA peut recevoir une petite commission (quelques pourcentages) 
                qui nous aide à financer le temps passé à tester et préparer ce guide.
              </p>
              <p className="text-blue-700 leading-relaxed">
                Cependant, nous vous encourageons toujours à privilégier vos commerces de proximité quand c'est possible : 
                quincailleries, jardineries, artisans et entrepreneurs de votre ville. Soutenir l'économie locale reste la meilleure option.
              </p>
              <div className="bg-blue-100 rounded-lg p-4">
                <p className="text-blue-800 text-sm">
                  <strong>💡 Astuce Amazon que presque personne ne connaît :</strong> vérifiez systématiquement l'option "D'Occasion – Comme neuf". 
                  Dans 9 cas sur 10, le produit est totalement neuf, seul l'emballage est un peu abîmé. 
                  Résultat : vous économisez 10 à 30 % pour exactement le même produit. C'est bon pour votre porte-monnaie… et pour la planète.
                </p>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Sommaire Principal */}
      <section className="py-12 lg:py-16 bg-gradient-to-br from-amber-50 to-yellow-50 border-y-4 border-amber-300">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-12">
            <h2 className="text-2xl lg:text-4xl font-bold text-amber-900 mb-3 lg:mb-4 flex items-center justify-center gap-2 lg:gap-3">
              <BookOpen className="w-6 h-6 lg:w-8 lg:h-8" />
              Sommaire du Guide
            </h2>
            <p className="text-sage-600 text-sm lg:text-lg">
              Cliquez sur un chapitre pour y accéder directement
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 lg:gap-4">
            {chapters.map((chapter, idx) => (
              <button
                key={chapter.id}
                onClick={() => scrollToChapter(chapter.id)}
                className="bg-white hover:bg-amber-50 border-2 border-amber-200 hover:border-amber-400 rounded-xl p-4 lg:p-6 transition-all text-left group shadow-sm hover:shadow-md"
              >
                <div className="flex items-start gap-3 lg:gap-4">
                  <span className="text-3xl lg:text-4xl flex-shrink-0">{chapter.icon}</span>
                  <div className="flex-1 min-w-0">
                    <h3 className="font-bold text-sage-900 mb-1 lg:mb-2 group-hover:text-amber-700 transition-colors text-sm lg:text-base">
                      {chapter.title}
                    </h3>
                    <div className="flex items-center gap-2 text-xs lg:text-sm text-green-600 font-medium">
                      <TrendingDown className="w-3 h-3 lg:w-4 lg:h-4" />
                      <span>{chapter.savings}</span>
                    </div>
                  </div>
                </div>
              </button>
            ))}
          </div>

          {/* Lien Produits Testés */}
          <div className="mt-6 lg:mt-8 text-center">
            <Link
              to="/fr/produits-testes"
              className="inline-flex items-center gap-2 lg:gap-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-bold px-6 py-3 lg:px-8 lg:py-4 rounded-xl transition-all shadow-lg hover:shadow-xl text-sm lg:text-base"
            >
              <ShoppingCart className="w-5 h-5 lg:w-6 lg:h-6" />
              <span>Voir les Produits Testés SAVEA</span>
              <ExternalLink className="w-4 h-4 lg:w-5 lg:h-5" />
            </Link>
          </div>
        </div>
      </section>

      {/* CHAPITRE 1 COMPLET - Énergie & logement */}
      <section id="chapitre-1" className="py-20 bg-gradient-to-br from-yellow-50 to-orange-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-orange-900 mb-4">
              ⚡ Chapitre 1 – Énergie & logement
            </h2>
            <div className="text-sm text-orange-600 mb-4">
              📅 Publié par SAVEA - Novembre 2024
            </div>
            <p className="text-xl text-orange-700">
              Les astuces les plus rentables pour réduire vos factures énergétiques
            </p>
          </div>

          {/* CTA Expert SAVEA en début de chapitre */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-4 lg:p-8 text-white text-center mb-12">
            <h3 className="font-bold text-lg lg:text-2xl mb-3 lg:mb-4">💡 Nos experts SAVEA vous accompagnent</h3>
            <p className="text-blue-100 text-sm lg:text-lg mb-4 lg:mb-6">
              Avant de lire ce chapitre, sachez que nos spécialistes énergie peuvent vous aider gratuitement 
              à comparer les offres et optimiser votre contrat selon votre consommation
            </p>
            <a 
              href={`tel:${getSaveaPhoneTel('FR', 'electricity', 'LP')}`}
              className="inline-flex items-center justify-center w-full lg:w-auto bg-white text-blue-600 hover:bg-blue-50 font-bold text-sm lg:text-lg px-4 lg:px-8 py-3 lg:py-4 rounded-lg"
            >
              <Phone className="w-5 h-5 lg:w-6 lg:h-6 mr-2" />
              <span className="hidden sm:inline">Expert Énergie SAVEA - <span className="savea-phone-number">{saveaPhone}</span></span>
              <span className="sm:hidden savea-phone-number">Appeler - {saveaPhone}</span>
            </a>
            <div className="text-blue-200 text-xs lg:text-sm mt-3">
              Service gratuit - Lundi à Samedi 8h-21h
            </div>
          </div>

          {/* 1.1 Changer de fournisseur */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  🔌 Changer de fournisseur d'électricité et/ou de gaz
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">15 minutes</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">150 à 300 €/an minimum</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  C'est LA première chose à vérifier. Si votre contrat date de plus d'un an, il a déjà augmenté, et vous payez trop cher. 
                  Le marché est fait pour que ceux qui ne bougent pas paient la facture. Or, changer de fournisseur est gratuit, 
                  se fait en ligne en quelques minutes et la résiliation est automatique.
                </p>
              </div>

              {/* CTA SAVEA recommandation */}
              <div className="bg-gradient-to-r from-sage-600 to-sage-700 rounded-xl p-4 lg:p-8 text-white text-center">
                <h4 className="font-bold text-lg lg:text-2xl mb-3 lg:mb-4">👉 Recommandation SAVEA</h4>
                <p className="text-sage-100 text-sm lg:text-lg mb-4 lg:mb-6">
                  Appelez nos experts SAVEA pour faire le point avec nos conseillers énergie. 
                  Ils vous aident gratuitement à comparer et choisir le meilleur fournisseur.
                </p>
                <a 
                  href={`tel:${getSaveaPhoneTel('FR', 'electricity', 'LP')}`}
                  className="inline-flex items-center justify-center w-full lg:w-auto bg-white text-sage-700 hover:bg-sage-50 font-bold text-sm lg:text-lg px-4 lg:px-8 py-3 lg:py-4 rounded-lg"
                >
                  <Phone className="w-5 h-5 lg:w-6 lg:h-6 mr-2" />
                  <span className="hidden sm:inline">Appeler Expert - <span className="savea-phone-number">{saveaPhone}</span></span>
                  <span className="sm:hidden savea-phone-number">{saveaPhone}</span>
                </a>
                <div className="text-sage-200 text-xs lg:text-sm mt-3">
                  Service gratuit - Spécialistes formés aux contrats énergétiques
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 1.2 Heures creuses */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  ⏰ Mettre ses appareils en heures creuses
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">10 minutes</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">80 à 150 €/an (selon consommation)</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  La tarification heures pleines / heures creuses existe encore chez EDF et d'autres fournisseurs. 
                  Si vous avez ce contrat, vous payez environ 30 % moins cher le kWh en heures creuses 
                  (souvent entre 22h et 6h, mais les plages varient selon les fournisseurs).
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">📊 Exemple concret testé SAVEA :</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-white rounded-lg p-4 text-center">
                    <div className="font-bold text-red-600 text-lg">Heures pleines</div>
                    <div className="text-red-700">Machine 2 kWh = 0,48 €</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center">
                    <div className="font-bold text-green-600 text-lg">Heures creuses</div>
                    <div className="text-green-700">Machine 2 kWh = 0,34 €</div>
                  </div>
                </div>
                <p className="text-blue-700">
                  Sur une année, en programmant lave-linge, chauffe-eau et lave-vaisselle, on peut économiser 80 à 150 €.
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <h4 className="font-bold text-yellow-800 text-lg mb-4">🛒 Produits testés SAVEA</h4>
                <p className="text-yellow-700 mb-4">
                  Si vos appareils n'ont pas de programmateur intégré, achetez une prise ou un fusible connecté :
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h5 className="font-bold text-sage-900 mb-2">Prise Tapo</h5>
                    <p className="text-sage-700 text-sm mb-3">
                      Fiable et facile à programmer lorsque l'appareil se branche à une prise classique. 
                      Testée et validée par nos équipes SAVEA.
                    </p>
                    <a 
                      href="https://amzn.to/46j4J8z"
                      target="_blank"
                      className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Voir sur Amazon
                    </a>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h5 className="font-bold text-sage-900 mb-2">Fusible WiFi Tuya</h5>
                    <p className="text-sage-700 text-sm mb-3">
                      Plus technique mais parfait pour chauffe-eau relié au compteur électrique. 
                      Installation électricien (quelques dizaines d'euros) puis pilotable smartphone avec app Tuya.
                    </p>
                    <a 
                      href="https://amzn.to/46P0tO1"
                      target="_blank"
                      className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Voir sur Amazon
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-sage-50 border border-sage-200 rounded-xl p-6">
                <h4 className="font-bold text-sage-800 text-lg mb-4">📌 Liens utiles fournisseurs</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <a 
                    href="https://particulier.edf.fr/fr/accueil/guide-energie/electricite/comprendre-heures-creuses.html"
                    target="_blank"
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800 bg-white rounded-lg p-3 border"
                  >
                    <ExternalLink className="w-4 h-4" />
                    EDF détaille ses plages horaires
                  </a>
                  <a 
                    href="https://octopusenergy.fr/aide-faq/mon-contrat/mon-option-tarifaire/trouver-horaires-heures-creuses"
                    target="_blank"
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800 bg-white rounded-lg p-3 border"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Horaires Octopus Energy
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 1.3 Ventilateur vs climatisation */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                  🌬️ Utiliser un ventilateur au lieu d'une climatisation
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Très facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">15 minutes</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">150 à 200 €/an</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Une climatisation consomme 1 à 2 kWh par heure. En été, 8h de clim par jour, c'est plus de 30 € par mois d'électricité.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">
                  👉 Nos équipes SAVEA ont cherché des alternatives et choisi le ventilateur Amazon Basics :
                </h4>
                <ul className="text-blue-700 space-y-2 mb-4">
                  <li>• <strong>Silencieux</strong> sur les premières vitesses, parfait pour dormir</li>
                  <li>• <strong>Livré avec télécommande</strong></li>
                  <li>• <strong>Prix abordable</strong> (30-40 €)</li>
                  <li>• <strong>Consommation ridicule :</strong> 0,05 kWh par heure, soit 0,01 € la nuit</li>
                </ul>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="font-bold text-sage-900">Ventilateur Amazon Basics testé SAVEA</div>
                      <div className="text-sage-600 text-sm">Silencieux + télécommande + économique</div>
                    </div>
                    <a 
                      href="https://amzn.to/4o2UY4r"
                      target="_blank"
                      className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4 inline mr-2" />
                      Modèle testé
                    </a>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 1.4 Réducteurs débit */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                  🚿 Installer des réducteurs de débit douche et robinets
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">5 min/robinet</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">150 à 250 €/an (eau + chauffe-eau)</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  L'eau chaude coûte cher car vous payez à la fois l'eau et l'électricité (ou le gaz) pour la chauffer. 
                  Un réducteur de débit divise la consommation par deux, sans perte de confort.
                </p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <p className="text-green-800 font-medium mb-4">
                  👉 Nos équipes SAVEA ont installé ces systèmes et franchement, on ne sent aucune différence sous la douche. 
                  Le confort est identique, mais la facture baisse immédiatement.
                </p>

                <h4 className="font-bold text-green-800 text-lg mb-4">📌 Produits testés et validés SAVEA :</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="font-bold text-sage-900 mb-2">Réducteur de débit douche</div>
                    <div className="text-sage-600 text-sm mb-3">
                      Vous choisissez le débit voulu en fonction de la couleur. Installation 5 minutes.
                    </div>
                    <a 
                      href="https://amzn.to/4nny8Ve"
                      target="_blank"
                      className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Voir sur Amazon
                    </a>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="font-bold text-sage-900 mb-2">Mousseur Robinet</div>
                    <div className="text-sage-600 text-sm mb-3">
                      Avec débit réglable ! Parfait pour évier cuisine et lavabo.
                    </div>
                    <a 
                      href="https://amzn.to/47OuL4C"
                      target="_blank"
                      className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Voir sur Amazon
                    </a>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 1.5 Filtration Brita Tap */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                    <Gift className="w-6 h-6 text-white" />
                  </div>
                  💧 Filtration d'eau avec Brita Tap
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">15 minutes</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">150 à 250 €/an</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Si vous achetez de l'eau en bouteille, vous gaspillez de l'argent et du plastique 
                  (et vous buvez des micro-plastiques et/ou des pesticides). Le système Brita Tap se fixe directement 
                  sur votre robinet et filtre l'eau du robinet. Résultat : une eau claire, désinfectée et reminéralisée 
                  et pas de carafe en plastique moche !
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-4 mb-4">
                  <h4 className="font-bold text-yellow-800 mb-2">⚠️ Important testé SAVEA :</h4>
                  <p className="text-yellow-700 text-sm">
                    Nos équipes ont testé plusieurs modèles, et celui-ci est le seul qui filtre vraiment tout. 
                    <strong>Attention : il faut utiliser les filtres VM-F</strong>, sinon le résultat est médiocre et n'enlève que le goût.
                  </p>
                </div>

                <h4 className="font-bold text-blue-800 text-lg mb-4">📌 Liens utiles testés SAVEA :</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
                    <div className="font-bold text-sage-900 mb-2">Brita Tap</div>
                    <div className="text-sage-600 text-sm mb-3">Système principal fixation robinet</div>
                    <a 
                      href="https://amzn.to/46kmvIx"
                      target="_blank"
                      className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors w-full justify-center"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Brita Tap
                    </a>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-gray-200 text-center">
                    <div className="font-bold text-sage-900 mb-2">Filtres VMF</div>
                    <div className="text-sage-600 text-sm mb-3">Filtres haute performance indispensables</div>
                    <a 
                      href="https://amzn.to/3Ky3d9P"
                      target="_blank"
                      className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors w-full justify-center"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Filtres VMF
                    </a>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 1.6 Piscine volet roulant */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-blue-600 rounded-xl flex items-center justify-center">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                  🏊 Piscine : installer un volet roulant
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-orange-100 text-orange-800 px-3 py-1">Installation pro</Badge>
                  <Badge className="bg-purple-100 text-purple-800 px-3 py-1">Investissement long terme</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">plusieurs centaines d'euros/an</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Si vous avez une piscine, c'est l'investissement le plus rentable. 
                  Nos équipes ont observé les différences énormes dans les installations :
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">✅ Avantages observés SAVEA :</h4>
                <ul className="text-blue-700 space-y-2 list-disc pl-6">
                  <li><strong>Moins d'évaporation</strong> (indispensable en période de sécheresse et notamment en cas d'interdiction de remplir les piscines, l'eau ne s'évapore plus et votre piscine peut continuer de fonctionner)</li>
                  <li><strong>Moins de produits chimiques</strong> nécessaires</li>
                  <li><strong>Moins de passages du pisciniste</strong></li>
                  <li><strong>Ouverture/fermeture simplifiée</strong></li>
                  <li><strong>Pas besoin de grillage</strong> autour de la piscine ou d'alarme si vous prenez un modèle incluant des sécurités enfants</li>
                </ul>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="text-green-800 font-medium">
                  👉 <strong>Résultat :</strong> l'entretien coûte beaucoup moins cher, la piscine reste propre plus longtemps 
                  et surtout, vous pouvez la garder même en cas de restrictions d'eau.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Conclusion Chapitre 1 */}
          <Card className="bg-gradient-to-r from-yellow-50 to-orange-50 border-yellow-300 border-2">
            <CardContent className="p-8">
              <h3 className="font-bold text-orange-800 text-2xl mb-4">✅ Conclusion Chapitre 1</h3>
              <p className="text-orange-700 text-lg leading-relaxed">
                Avec quelques gestes simples (changer de fournisseur, programmer ses appareils, installer des réducteurs ou un filtre à eau) 
                et des choix malins comme un ventilateur plutôt qu'une climatisation, vous pouvez réduire vos factures sans perdre en confort. 
                Pour les plus motivés, un panneau solaire ou un volet de piscine sont des investissements qui paient sur le long terme.
              </p>
              <div className="bg-orange-100 rounded-lg p-4 mt-4">
                <div className="text-center">
                  <div className="font-bold text-orange-800 text-xl">Résultat : 500 à 1 000 € d'économies par an, sans se priver</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CHAPITRE 2 COMPLET - Internet & téléphonie */}
      <section id="chapitre-2" className="py-20 bg-gradient-to-br from-blue-50 to-cyan-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-blue-900 mb-4">
              📡 Chapitre 2 – Internet & téléphonie
            </h2>
            <div className="text-sm text-blue-600 mb-4">
              📅 Publié par SAVEA - Décembre 2024
            </div>
            <p className="text-xl text-blue-700">
              Les astuces pour payer votre internet et téléphone au juste prix
            </p>
          </div>

          {/* CTA Expert SAVEA Internet */}
          <div className="bg-gradient-to-r from-cyan-600 to-blue-700 rounded-2xl p-4 lg:p-8 text-white text-center mb-12">
            <h3 className="font-bold text-lg lg:text-2xl mb-3 lg:mb-4">📞 Nos experts internet SAVEA vous accompagnent</h3>
            <p className="text-cyan-100 text-sm lg:text-lg mb-4 lg:mb-6">
              Nos spécialistes internet vous aident gratuitement à comparer les offres, négocier avec opérateurs 
              et optimiser vos contrats selon vos besoins
            </p>
            <Button 
              onClick={() => window.open(`tel:${getSaveaPhoneTel('FR', 'internet', 'LP')}`)}
              size="lg"
              className="w-full lg:w-auto bg-white text-cyan-600 hover:bg-cyan-50 font-bold text-sm lg:text-lg px-4 lg:px-8 py-3 lg:py-4"
            >
              <Phone className="w-5 h-5 lg:w-6 lg:h-6 mr-2" />
              <span className="hidden sm:inline">Expert Internet - {getSaveaPhone('FR', 'internet', 'LP')}</span>
              <span className="sm:hidden">{getSaveaPhone('FR', 'internet', 'LP')}</span>
            </Button>
            <div className="text-cyan-200 text-xs lg:text-sm mt-3">
              Service gratuit - Spécialistes négociation opérateurs
            </div>
          </div>

          {/* 2.1 Résiliation facile */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center">
                    <Wifi className="w-6 h-6 text-white" />
                  </div>
                  📡 Résiliation facile grâce aux lois récentes
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">15 minutes</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">100 à 200 €/an</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Beaucoup de gens croient encore qu'ils sont prisonniers de leur fournisseur Internet. 
                  C'était vrai il y a dix ans, mais aujourd'hui la loi a changé : la résiliation est beaucoup plus simple.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">👉 Deux cas analysés SAVEA :</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-green-300">
                    <h5 className="font-bold text-green-700 mb-3">✅ Vous n'êtes plus engagé :</h5>
                    <p className="text-green-700 text-sm">
                      La résiliation est totalement gratuite, et elle est même automatique si vous changez de fournisseur 
                      (comme pour l'électricité).
                    </p>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-orange-300">
                    <h5 className="font-bold text-orange-700 mb-3">⏰ Vous êtes encore engagé :</h5>
                    <p className="text-orange-700 text-sm">
                      Le premier moment pour résilier sans frais est la fin de votre engagement. Mais si votre abonnement est vraiment trop cher, 
                      il peut être rentable de résilier avant, même avec des frais. L'économie d'un nouveau contrat compense souvent largement le coût.
                    </p>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <h4 className="font-bold text-yellow-800 text-lg mb-4">⚡ Astuce SAVEA :</h4>
                <p className="text-yellow-700 leading-relaxed mb-4">
                  Certains fournisseurs exigent encore une lettre recommandée. Pas de panique : vous n'avez pas besoin d'imprimer, 
                  de poster ou de vous déplacer. Des sites comme lettre-resiliation.com vous permettent de remplir un modèle en ligne, 
                  payer environ 2 €, et ils se chargent de l'envoyer à votre place.
                </p>
                <a 
                  href="https://www.lettre-resiliation.com/"
                  target="_blank"
                  className="inline-flex items-center gap-2 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  lettre-resiliation.com
                </a>
              </div>
            </CardContent>
          </Card>

          {/* 2.2 Combien payer */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                    <TrendingDown className="w-6 h-6 text-white" />
                  </div>
                  💰 Combien devez-vous payer ?
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">30 min</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">150 à 300 €/an</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Le marché de l'Internet est extrêmement concurrentiel. Les opérateurs se battent à coups de promotions permanentes 
                  pour attirer de nouveaux clients. Cela veut dire une chose : il faut changer régulièrement 
                  (idéalement chaque année) pour profiter des meilleurs prix.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">💰 Prix de référence SAVEA à retenir :</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="bg-white rounded-lg p-4 text-center border border-gray-200">
                    <div className="font-bold text-blue-700 text-xl">≤ 25 €/mois</div>
                    <div className="text-blue-600">Box Internet seule</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center border border-gray-200">
                    <div className="font-bold text-blue-700 text-xl">≤ 30 €/mois</div>
                    <div className="text-blue-600">Box + TV</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 text-center border border-gray-200">
                    <div className="font-bold text-blue-700 text-xl">≤ 10 €/mois</div>
                    <div className="text-blue-600">Ligne mobile</div>
                    <div className="text-blue-500 text-xs mt-1">(4G largement suffisante)</div>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <h4 className="font-bold text-green-800 text-lg mb-4">📊 Exemple concret testé SAVEA :</h4>
                <div className="space-y-2 text-green-700">
                  <div>• Chez Bouygues Telecom, offre B&You : <strong>Box + TV pour 30 €</strong></div>
                  <div>• Vous pouvez ajouter <strong>ligne mobile 4G 80 Go pour 8 €/mois</strong></div>
                  <div>• <strong>Total : 37 €/mois au total, sans engagement</strong></div>
                </div>
                <div className="bg-green-100 rounded-lg p-4 mt-4">
                  <p className="text-green-800 font-bold">
                    👉 Si vous payez 50, 60 ou 70 € pour la même chose ailleurs, vous êtes perdant.
                  </p>
                </div>
                <a 
                  href="https://www.bouyguestelecom.fr/forfaits-mobiles/sans-engagement?pure_fibre=true"
                  target="_blank"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors mt-4"
                >
                  <ExternalLink className="w-4 h-4" />
                  Offres Bouygues Telecom
                </a>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                <h4 className="font-bold text-purple-800 text-lg mb-4">💝 Astuce solidaire :</h4>
                <p className="text-purple-700 leading-relaxed mb-4">
                  Bouygues Telecom propose aussi le don de giga. Chaque client peut donner une partie de ses gigas inutilisés 
                  à des associations qui les redistribuent à des personnes dans le besoin. 
                  Une bonne manière de ne rien gaspiller et d'aider concrètement.
                </p>
                <a 
                  href="https://www.bouyguestelecom.fr/choisir-bouygues-telecom/don-de-giga"
                  target="_blank"
                  className="inline-flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Don de giga Bouygues
                </a>
              </div>
            </CardContent>
          </Card>

          {/* 2.3 Voyager sans hors forfait */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                    <Plane className="w-6 h-6 text-white" />
                  </div>
                  🌍 Voyager sans hors forfait
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">15 minutes</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">50 à 200 €/voyage</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Les factures astronomiques de roaming appartiennent au passé. Depuis 2017, grâce à la législation européenne, 
                  vos forfaits mobiles fonctionnent comme en France partout dans l'Union européenne.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">🌍 Hors UE, solutions testées SAVEA :</h4>
                <p className="text-blue-700 mb-4">
                  <strong>Exemple concret testé :</strong> pour deux semaines au Maroc, nos équipes ont utilisé Holafly : 
                  une eSIM illimitée pour environ 3€/jour.
                </p>

                <div className="bg-white rounded-lg p-4 border border-gray-200 mb-4">
                  <h5 className="font-bold text-blue-800 mb-3">⚡ Qu'est-ce qu'une eSIM ?</h5>
                  <p className="text-blue-700 text-sm">
                    C'est tout simplement une carte SIM dématérialisée qui s'installe directement dans votre smartphone via un QR code. 
                    Plus besoin de carte physique, et vous pouvez en cumuler plusieurs (parfait si vous gardez votre numéro français).
                  </p>
                </div>

                <h5 className="font-bold text-blue-800 mb-3">👉 Mode d'emploi simple testé SAVEA :</h5>
                <ol className="text-blue-700 space-y-2 list-decimal pl-6 mb-4">
                  <li>Installez la eSIM depuis l'app Holafly</li>
                  <li>Désactivez les données mobiles de votre forfait habituel pour éviter tout dépassement</li>
                  <li>Utilisez WhatsApp, Facetime ou Google Phone pour vos appels</li>
                </ol>

                <a 
                  href="https://esim.holafly.com/fr/"
                  target="_blank"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Holafly eSIM
                </a>
              </div>
            </CardContent>
          </Card>

          {/* 2.4 Forfaits mobiles malins */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  📞 Les forfaits mobiles malins
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">20 minutes</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">100 à 200 €/an</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Voici deux forfaits incontournables testés SAVEA si vous voulez payer le juste prix :
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                  <h4 className="font-bold text-red-800 text-lg mb-4">📱 Free 2 €</h4>
                  <p className="text-red-700 leading-relaxed mb-4">
                    Parfait pour garder un numéro actif à moindre coût (utile pour les enfants ou si vous partez longtemps à l'étranger).
                  </p>
                  <a 
                    href="https://mobile.free.fr/fiche-forfait-2-euros"
                    target="_blank"
                    className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Forfait Free 2€
                  </a>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <h4 className="font-bold text-blue-800 text-lg mb-4">✈️ Free Série Free à 10€</h4>
                  <p className="text-blue-700 leading-relaxed mb-4">
                    Pendant 1 an (changement obligatoire !) : le meilleur choix si vous voyagez plusieurs mois par an, 
                    car il inclut l'utilisation dans de nombreux pays étrangers comme si vous étiez en France.
                  </p>
                  <div className="bg-blue-100 rounded-lg p-3 mb-4">
                    <p className="text-blue-800 text-sm">
                      👉 <strong>Astuce :</strong> si vous partez vivre plusieurs années à l'étranger, 
                      ce forfait peut vous permettre de continuer à utiliser votre téléphone comme si vous étiez resté en France.
                    </p>
                  </div>
                  <a 
                    href="https://mobile.free.fr/fiche-forfait-serie-free"
                    target="_blank"
                    className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Forfait Free Série
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 2.5 Double SIM */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  📲 Deux numéros sur un seul téléphone
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Très facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">Immédiat</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-blue-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-blue-800">Confort et praticité (pas d'économie directe)</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  De nombreux téléphones modernes permettent la double SIM (physique + eSIM). 
                  C'est très pratique si vous voulez séparer vie pro et perso, ou garder un numéro français 
                  tout en utilisant une carte locale à l'étranger.
                </p>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                <h4 className="font-bold text-purple-800 text-lg mb-4">💡 Bonus astuce SAVEA :</h4>
                <p className="text-purple-700 leading-relaxed">
                  Et si vous voulez deux comptes WhatsApp différents sur le même téléphone : 
                  installez l'app WhatsApp classique + WhatsApp Business. 
                  Elles fonctionnent de la même façon, mais chacune avec un numéro différent.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Conclusion Chapitre 2 */}
          <Card className="bg-gradient-to-r from-blue-50 to-cyan-50 border-blue-300 border-2">
            <CardContent className="p-8">
              <h3 className="font-bold text-blue-800 text-2xl mb-4">✅ Conclusion Chapitre 2</h3>
              <p className="text-blue-700 text-lg leading-relaxed">
                Dans les télécoms, la fidélité ne paie pas. Comparez, changez, résiliez si besoin : c'est gratuit la plupart du temps, 
                et même quand des frais existent, ils peuvent être compensés par les économies d'un nouveau contrat.
              </p>
              <div className="bg-blue-100 rounded-lg p-4 mt-4">
                <div className="text-center">
                  <div className="font-bold text-blue-800 text-xl">Résultat : 150 à 300 € économisés par an, sans effort particulier</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CHAPITRE 3 COMPLET - Assurances */}
      <section id="chapitre-3" className="py-20 bg-gradient-to-br from-purple-50 to-pink-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-purple-900 mb-4">
              🛡️ Chapitre 3 – Assurances
            </h2>
            <div className="text-sm text-purple-600 mb-4">
              📅 Publié par SAVEA - Janvier 2026
            </div>
            <p className="text-xl text-purple-700">
              Optimiser vos assurances sans perdre en protection réelle
            </p>
          </div>

          {/* 3.1 Retour investissement */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                    <TrendingDown className="w-6 h-6 text-white" />
                  </div>
                  🛡️ Penser en retour sur investissement
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-orange-100 text-orange-800 px-3 py-1">Moyenne</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">30 min réflexion</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">200 à 800 €/an</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Avant de souscrire une assurance, il est essentiel de penser en termes de retour sur investissement.
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <h4 className="font-bold text-yellow-800 text-lg mb-4">📊 Exemple analysé SAVEA :</h4>
                <div className="text-yellow-700 space-y-3 text-lg">
                  <div>• Assurance auto tous risques vol : <strong>+60 €/mois</strong> vs formule tiers</div>
                  <div>• 60 € × 12 mois = <strong>720 €/an</strong></div>
                  <div>• Sur 5 ans = <strong>3 600 €</strong></div>
                </div>
                <div className="bg-yellow-100 rounded-lg p-4 mt-4">
                  <p className="font-bold text-yellow-800">
                    Autrement dit, vous aurez payé la moitié du prix d'une voiture d'occasion uniquement en assurance !
                  </p>
                </div>
                <p className="text-yellow-700 mt-4">
                  Il faut vraiment garder cette logique en tête : certaines couvertures rassurent, mais coûtent bien plus cher 
                  que la valeur réelle de ce qu'elles protègent.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 3.2 Assurances inutiles */}
          <Card className="mb-12">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-xl">
                <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                📦 Attention aux assurances inutiles
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Beaucoup de distributeurs essaient de vous vendre des assurances supplémentaires pour vos appareils électroménagers, 
                  téléphones ou ordinateurs. En réalité, elles ne servent presque jamais : 
                  <strong>la loi européenne vous protège déjà avec une garantie légale de conformité de 2 ans sur tous vos achats.</strong>
                </p>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <h4 className="font-bold text-green-800 text-lg mb-4">✅ Ce que dit la loi européenne :</h4>
                <p className="text-green-700 leading-relaxed mb-4">
                  👉 <strong>Concrètement :</strong> si un produit tombe en panne dans les deux ans suivant l'achat, 
                  le vendeur est obligé de le réparer, de l'échanger ou de le rembourser gratuitement.
                </p>
                <p className="text-green-700 leading-relaxed">
                  👉 Les assurances complémentaires (casse, panne, extension de garantie) reviennent donc souvent 
                  à payer pour une couverture que vous avez déjà.
                </p>
                <div className="bg-green-100 rounded-lg p-4 mt-4">
                  <p className="text-green-800 font-medium">
                    <strong>Conseil SAVEA :</strong> avant de souscrire une assurance supplémentaire, 
                    demandez-vous toujours si elle apporte une vraie protection supplémentaire… 
                    ou si c'est juste une manière de vous faire payer deux fois.
                  </p>
                </div>
                <a 
                  href="https://www.service-public.fr/particuliers/vosdroits/F11094"
                  target="_blank"
                  className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors mt-4"
                >
                  <ExternalLink className="w-4 h-4" />
                  Explications garantie légale conformité
                </a>
              </div>
            </CardContent>
          </Card>

          {/* 3.3 Assurance habitation */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                  🏠 Assurance habitation
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">1h comparer</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">100 à 300 €/an</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  L'assurance habitation est indispensable (elle est même obligatoire si vous êtes locataire). 
                  Mais les prix varient énormément selon les assureurs.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">👉 Conseils pratiques SAVEA :</h4>
                <ul className="text-blue-700 space-y-2 list-disc pl-6 mb-4">
                  <li>Faites vos démarches en ligne (plus simple que par téléphone ou courrier)</li>
                  <li>Demandez des devis comparatifs régulièrement</li>
                  <li>N'hésitez pas à renégocier votre contrat en mettant en avant les prix de la concurrence</li>
                </ul>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <h5 className="font-bold text-blue-800 mb-2">📌 Exemple testé SAVEA :</h5>
                  <p className="text-blue-700 mb-3">
                    La MAIF propose des couvertures à partir d'environ 300 €/an.
                  </p>
                  <a 
                    href="https://www.maif.fr/habitation/assurance-habitation"
                    target="_blank"
                    className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Devis en ligne MAIF
                  </a>
                </div>
              </div>

              {/* CTA Template email SAVEA */}
              <div className="bg-gradient-to-r from-amber-600 to-orange-600 rounded-xl p-8 text-white text-center">
                <h4 className="font-bold text-2xl mb-4">📧 Template de négociation SAVEA</h4>
                <p className="text-amber-100 text-lg mb-6">
                  Utilisez notre modèle d'email efficace pour renégocier votre assurance habitation et obtenir un geste commercial
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    asChild
                    className="bg-white text-amber-600 hover:bg-amber-50 font-bold px-6 py-3"
                  >
                    <Link to="/fr/modeles-emails">
                      📧 Voir Template Assurance Habitation
                    </Link>
                  </Button>
                  <Button 
                    onClick={() => window.open(`tel:${getSaveaPhoneTel('FR', 'electricity', 'LP')}`)}
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-amber-600 font-bold px-6 py-3"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Aide Expert SAVEA
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 3.4 Assurance auto */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center">
                    <Car className="w-6 h-6 text-white" />
                  </div>
                  🚗 Assurance auto & moto
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">1-2h comparer</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">200 à 600 €/an</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Même conseil que pour l'habitation : ne restez pas fidèle par habitude. 
                  Chaque année, la plupart des assureurs appliquent une augmentation automatique. Vous avez deux solutions :
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <h4 className="font-bold text-blue-800 text-lg mb-4">1️⃣ Refuser la hausse et renégocier</h4>
                  <p className="text-blue-700">
                    Utilisez nos templates de négociation SAVEA pour obtenir un geste commercial.
                  </p>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                  <h4 className="font-bold text-green-800 text-lg mb-4">2️⃣ Changer d'assureur</h4>
                  <p className="text-green-700">
                    Comparez les offres et changez si l'écart est significatif.
                  </p>
                </div>
              </div>

              {/* CTA Template auto SAVEA */}
              <div className="bg-gradient-to-r from-red-600 to-rose-600 rounded-xl p-8 text-white text-center">
                <h4 className="font-bold text-2xl mb-4">📧 Template auto/moto SAVEA</h4>
                <p className="text-red-100 text-lg mb-6">
                  Modèle d'email efficace pour renégocier votre assurance auto ou moto et éviter les augmentations automatiques
                </p>
                <Button 
                  asChild
                  className="bg-white text-red-600 hover:bg-red-50 font-bold px-6 py-3"
                >
                  <Link to="/fr/modeles-emails">
                    📧 Voir Template Assurance Auto/Moto
                  </Link>
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* 3.5 Mutuelles */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-pink-500 rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  💊 Mutuelles & santé
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-orange-100 text-orange-800 px-3 py-1">Moyenne</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">1h comparer</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">200 à 400 €/an</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  L'assurance santé (mutuelle) est un autre sujet délicat. Quelques points importants analysés par SAVEA :
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                  <h4 className="font-bold text-yellow-800 text-lg mb-4">⏰ Délai de carence</h4>
                  <p className="text-yellow-700 text-sm leading-relaxed">
                    Certaines mutuelles imposent un délai avant de pouvoir utiliser les garanties 
                    (souvent plusieurs mois si vous n'aviez pas de mutuelle avant). En cas de besoin immédiat, 
                    ce délai peut poser problème. Heureusement, certaines mutuelles n'ont pas de délai de carence.
                  </p>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                  <h4 className="font-bold text-green-800 text-lg mb-4">💡 Mutuelles d'entreprise</h4>
                  <p className="text-green-700 text-sm leading-relaxed">
                    Souvent les plus avantageuses : elles sont négociées collectivement et une partie de la cotisation 
                    est payée par l'employeur. Même chose si vous pouvez être rattaché à la mutuelle de votre conjoint(e).
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Conclusion Chapitre 3 */}
          <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-purple-300 border-2">
            <CardContent className="p-8">
              <h3 className="font-bold text-purple-800 text-2xl mb-4">✅ Conclusion Chapitre 3</h3>
              <p className="text-purple-700 text-lg leading-relaxed">
                L'assurance est un poste de dépense incontournable, mais aussi un des plus faciles à optimiser sans perdre en protection réelle. 
                L'idée n'est pas de supprimer les assurances essentielles, mais de les payer au juste prix. 
                En renégociant régulièrement et en choisissant les bonnes options, vous pouvez économiser 200 à 800 €/an.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CHAPITRE 4 COMPLET - Banque & épargne */}
      <section id="chapitre-4" className="py-20 bg-gradient-to-br from-green-50 to-emerald-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-green-900 mb-4">
              💳 Chapitre 4 – Banque & épargne
            </h2>
            <div className="text-sm text-green-600 mb-4">
              📅 Publié par SAVEA - Février 2026
            </div>
            <p className="text-xl text-green-700">
              Optimiser vos finances et protéger votre épargne de l'inflation
            </p>
          </div>

          {/* 4.1 Audit frais bancaires */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                    <CreditCard className="w-6 h-6 text-white" />
                  </div>
                  💳 Faire l'audit de ses frais bancaires
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">1h</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">100 à 300 €/an</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  La première étape est simple : savoir combien vous coûte votre banque chaque mois. 
                  Beaucoup de gens paient sans s'en rendre compte des frais de tenue de compte, des commissions, 
                  ou des assurances liées aux cartes bancaires qu'ils n'utilisent même pas.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">👉 Deux options pour commencer :</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h5 className="font-bold text-blue-800 mb-2">1. Méthode directe</h5>
                    <p className="text-blue-700 text-sm">
                      Relever les frais directement dans vos relevés bancaires mensuels
                    </p>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h5 className="font-bold text-blue-800 mb-2">2. Demande officielle</h5>
                    <p className="text-blue-700 text-sm">
                      Écrire à votre conseiller pour demander un récapitulatif clair (cf. template SAVEA plus bas)
                    </p>
                  </div>
                </div>
              </div>

              {/* CTA Template banque SAVEA */}
              <div className="bg-gradient-to-r from-green-600 to-emerald-600 rounded-xl p-8 text-white text-center">
                <h4 className="font-bold text-2xl mb-4">📧 Template négociation bancaire SAVEA</h4>
                <p className="text-green-100 text-lg mb-6">
                  Utilisez notre modèle d'email détaillé pour demander un récapitulatif et optimisation de vos frais bancaires. 
                  Template complet et efficace inclus !
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Button 
                    asChild
                    className="bg-white text-green-600 hover:bg-green-50 font-bold px-6 py-3"
                  >
                    <Link to="/fr/modeles-emails">
                      📧 Voir Template Banque Complet
                    </Link>
                  </Button>
                  <Button 
                    onClick={() => window.open(`tel:${getSaveaPhoneTel('FR', 'electricity', 'LP')}`)}
                    variant="outline"
                    className="border-2 border-white text-white hover:bg-white hover:text-green-600 font-bold px-6 py-3"
                  >
                    <Phone className="w-4 h-4 mr-2" />
                    Aide Expert SAVEA
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 4.2 Livrets épargne */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                    <TrendingDown className="w-6 h-6 text-white" />
                  </div>
                  🏦 Ouvrir un livret d'épargne garanti par l'État
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">20 minutes</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Gain estimé</div>
                <div className="text-2xl font-bold text-green-800">2 à 4 %/an sur l'épargne disponible</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Ne laissez pas vos économies dormir sur votre compte courant : elles perdent de la valeur avec l'inflation. 
                  Placez-les sur un livret d'épargne réglementé, garanti par l'État :
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6 text-center">
                  <h4 className="font-bold text-blue-800 text-lg mb-3">Livret A</h4>
                  <div className="space-y-2 text-blue-700">
                    <div><strong>Plafond :</strong> 22 950 €</div>
                    <div><strong>Taux :</strong> 3 % actuellement</div>
                    <div><strong>Intérêts :</strong> exonérés d'impôt</div>
                  </div>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                  <h4 className="font-bold text-green-800 text-lg mb-3">LDDS</h4>
                  <div className="space-y-2 text-green-700">
                    <div><strong>Plafond :</strong> 12 000 €</div>
                    <div><strong>Taux :</strong> 3 %</div>
                    <div><strong>Intérêts :</strong> exonérés</div>
                  </div>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-xl p-6 text-center">
                  <h4 className="font-bold text-purple-800 text-lg mb-3">LEP</h4>
                  <div className="space-y-2 text-purple-700">
                    <div><strong>Plafond :</strong> 7 700 €</div>
                    <div><strong>Taux :</strong> 5 %</div>
                    <div><strong>Conditions :</strong> revenus modestes</div>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl p-4">
                <p className="text-green-700 text-center">
                  👉 C'est gratuit, 100 % sécurisé, et cela protège vos économies sans risque.
                </p>
                <div className="text-center mt-4">
                  <a 
                    href="https://www.service-public.fr/particuliers/vosdroits/F34394"
                    target="_blank"
                    className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Guide officiel service-public.fr
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 4.3 Changer de banque */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  🔄 Changer de banque facilement
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">1h</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">50 à 150 €/an</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Changer de banque n'est plus une galère : depuis la loi Macron de 2017, le service de mobilité bancaire est automatisé.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">✅ Ce qui est automatisé :</h4>
                <ul className="text-blue-700 space-y-2 list-disc pl-6">
                  <li><strong>Votre nouvelle banque se charge</strong> de transférer vos virements et prélèvements</li>
                  <li><strong>Pas besoin d'écrire</strong> à vos fournisseurs ou à votre employeur</li>
                  <li><strong>Résultat :</strong> vous pouvez facilement changer pour une banque plus compétitive</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* 4.4 Investir épargne */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                    <TrendingDown className="w-6 h-6 text-white" />
                  </div>
                  📈 Investir une partie de son épargne
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-orange-100 text-orange-800 px-3 py-1">Moyenne</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">2h setup</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">📈 Potentiel de rendement</div>
                <div className="text-2xl font-bold text-green-800">10 à 15 %/an (retour d'expérience, avant impôts)</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Une fois vos économies sécurisées, vous pouvez investir une partie sur les marchés financiers. 
                  Avec Trading 212, vous pouvez commencer dès 10 € :
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">✅ Avantages Trading 212 :</h4>
                <ul className="text-blue-700 space-y-2 list-disc pl-6 mb-4">
                  <li><strong>Application intuitive</strong> et accessible aux débutants</li>
                  <li><strong>Déclarations fiscales simplifiées</strong> pour les européens</li>
                  <li><strong>Fractions d'actions :</strong> ex. 10 € d'Apple au lieu d'une action entière</li>
                </ul>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <h4 className="font-bold text-red-800 text-lg mb-4">⚠️ Point important :</h4>
                <p className="text-red-700 leading-relaxed">
                  <strong>Investir en bourse comporte des risques.</strong> Vous pouvez perdre tout ou partie de votre capital. 
                  Ce n'est pas un conseil en investissement, mais un retour d'expérience. Nos équipes observent une rentabilité 
                  de 10 à 15 %/an avant impôts, mais rien n'est garanti.
                </p>
              </div>
            </CardContent>
          </Card>

          {/* 4.5 Banques en ligne */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  🌍 Les banques en ligne & néobanques
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">30 minutes</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">50 à 200 €/an</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Les banques en ligne (Boursorama, Hello Bank…) et les néobanques (N26, Revolut) proposent des services gratuits ou quasi gratuits.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">✅ Avantages testés SAVEA :</h4>
                <ul className="text-blue-700 space-y-2 list-disc pl-6 mb-4">
                  <li><strong>Pas de frais de tenue de compte</strong></li>
                  <li><strong>Cartes bancaires gratuites</strong> (souvent à usage courant)</li>
                  <li><strong>Offres de bienvenue :</strong> souvent quelques centaines d'euros pour ouvrir un compte gratuit</li>
                  <li><strong>Paiements en devises sans frais</strong> → très pratique en voyage</li>
                </ul>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <h5 className="font-bold text-blue-800 mb-2">📊 Exemple testé SAVEA :</h5>
                  <p className="text-blue-700 text-sm">
                    Avec Revolut ou N26, vous payez vos achats à l'étranger au taux réel du marché, 
                    sans les 2-3 % de commission que les banques traditionnelles prélèvent ou les taux de change des aéroports.
                  </p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <h4 className="font-bold text-yellow-800 text-lg mb-4">📌 Services testés SAVEA :</h4>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <a href="https://sumeria.eu/" target="_blank" className="bg-white rounded-lg p-4 border hover:shadow-md transition-shadow">
                    <div className="font-bold text-sage-900 text-center">Sumeria</div>
                    <div className="text-sage-600 text-xs text-center mt-1">(ancien Lydia)</div>
                  </a>
                  <a href="https://www.revolut.com/fr-FR" target="_blank" className="bg-white rounded-lg p-4 border hover:shadow-md transition-shadow">
                    <div className="font-bold text-sage-900 text-center">Revolut</div>
                    <div className="text-sage-600 text-xs text-center mt-1">Multi-devises</div>
                  </a>
                  <a href="https://n26.com/fr-fr" target="_blank" className="bg-white rounded-lg p-4 border hover:shadow-md transition-shadow">
                    <div className="font-bold text-sage-900 text-center">N26</div>
                    <div className="text-sage-600 text-xs text-center mt-1">100% mobile</div>
                  </a>
                </div>

                <div className="bg-yellow-100 rounded-lg p-4 mt-4">
                  <h5 className="font-bold text-yellow-800 text-sm mb-2">💡 Transparence sur le parrainage :</h5>
                  <p className="text-yellow-700 text-xs leading-relaxed">
                    Certaines banques ou applications que nous citons proposent des programmes de parrainage. 
                    Si vous ouvrez un compte via nos liens, SAVEA peut recevoir une petite rémunération. 
                    De votre côté, cela ne vous coûte rien de plus, et vous bénéficiez souvent d'un bonus de bienvenue. 
                    <strong>Nous ne recommandons que des services testés par nos équipes.</strong>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Conclusion Chapitre 4 */}
          <Card className="bg-gradient-to-r from-green-50 to-emerald-50 border-green-300 border-2">
            <CardContent className="p-8">
              <h3 className="font-bold text-green-800 text-2xl mb-4">✅ Conclusion Chapitre 4</h3>
              <p className="text-green-700 text-lg leading-relaxed">
                En auditant vos frais bancaires, en utilisant les livrets garantis, en n'hésitant pas à changer de banque, 
                et en explorant les options modernes (banques en ligne, néobanques, investissements), vous pouvez économiser 
                200 à 600 €/an, tout en protégeant vos économies de l'inflation.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CHAPITRE 5 COMPLET - Voiture & carburant */}
      <section id="chapitre-5" className="py-20 bg-gradient-to-br from-red-50 to-rose-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-red-900 mb-4">
              🚗 Chapitre 5 – Voiture & carburant
            </h2>
            <div className="text-sm text-red-600 mb-4">
              📅 Publié par SAVEA - Mars 2026
            </div>
            <p className="text-xl text-red-700">
              Réduire drastiquement vos frais automobiles
            </p>
          </div>

          {/* 5.1 Conversion E85 */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                    <Car className="w-6 h-6 text-white" />
                  </div>
                  ⛽ Conversion moteur à l'E85 (éthanol)
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile (par pro)</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">1h chez garagiste</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">~650 €/an pour 50 km quotidiens</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Le carburant E85 (bioéthanol) est désormais très répandu en France et présent dans quasiment toutes les stations. 
                  Il est vendu environ deux fois moins cher que le SP95 ou le SP98 !! 
                  La contrepartie, c'est que la consommation augmente légèrement (+15 à +25 %), 
                  mais même ainsi, l'économie reste très importante.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">📊 Exemple observé par nos équipes :</h4>
                <p className="text-blue-700 mb-4">
                  Nos équipes ont testé cette conversion : le moteur consomme un peu plus, 
                  mais comme l'éthanol coûte deux fois moins cher, la facture annuelle est beaucoup plus basse.
                </p>

                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <h5 className="font-bold text-blue-800 text-xl mb-4">📊 Simulation SAVEA pour 50 km/jour (13 000 km/an)</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="text-center p-4 bg-red-50 rounded-lg border border-red-200">
                      <div className="font-bold text-red-600 text-xl mb-2">SP95</div>
                      <div className="text-red-700 space-y-1">
                        <div>6,5 L/100 km à 1,85 €/L</div>
                        <div className="font-bold text-xl">→ ~1 560 €/an</div>
                      </div>
                    </div>
                    <div className="text-center p-4 bg-green-50 rounded-lg border border-green-200">
                      <div className="font-bold text-green-600 text-xl mb-2">E85</div>
                      <div className="text-green-700 space-y-1">
                        <div>7,8 L/100 km à 0,90 €/L</div>
                        <div className="font-bold text-xl">→ ~910 €/an</div>
                      </div>
                    </div>
                  </div>
                  <div className="text-center mt-6 p-4 bg-green-100 rounded-lg">
                    <div className="font-bold text-green-800 text-2xl">💰 Économie : environ 650 €/an</div>
                  </div>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <h4 className="font-bold text-yellow-800 text-lg mb-4">⚙️ Deux façons de rouler à l'E85</h4>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg p-4 border border-orange-300">
                    <h5 className="font-bold text-orange-800 mb-3">1. Reprogrammation électronique</h5>
                    <ul className="text-orange-700 text-sm space-y-1 list-disc pl-4">
                      <li><strong>Coût :</strong> 400 à 600 €</li>
                      <li>Très répandue, efficace, invisible au contrôle technique</li>
                      <li>⚠️ <strong>Pas homologuée officiellement :</strong> zone grise juridique</li>
                      <li>En cas d'accident grave, assurance pourrait contester (si au courant)</li>
                    </ul>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-green-300">
                    <h5 className="font-bold text-green-800 mb-3">2. Boîtier homologué E85 ✅</h5>
                    <ul className="text-green-700 text-sm space-y-1 list-disc pl-4">
                      <li><strong>Coût :</strong> 700 à 1 200 € (garage + carte grise)</li>
                      <li><strong>Reconnu par l'État</strong> → modification carte grise "FE" (FlexFuel)</li>
                      <li><strong>Votre assurance ne peut pas contester</strong></li>
                      <li>Avantages fiscaux régionaux possibles</li>
                    </ul>
                  </div>
                </div>

                <div className="bg-yellow-100 rounded-lg p-4 mt-4">
                  <p className="text-yellow-800 text-sm">
                    👉 Dans tous les cas, c'est sans risque mécanique majeur si le véhicule est compatible 
                    (la plupart des moteurs essence récents supportent l'E85). 
                    Mais mieux vaut confier la modification à un professionnel sérieux.
                  </p>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <h4 className="font-bold text-green-800 text-lg mb-4">📌 Liens utiles testés SAVEA :</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <a 
                    href="https://www.bioethanolcarburant.com/trouver-une-station-e85/"
                    target="_blank"
                    className="flex items-center gap-2 text-green-600 hover:text-green-800 bg-white rounded-lg p-3 border"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Carte des stations E85 en France
                  </a>
                  <a 
                    href="https://www.prix-carburants.gouv.fr/"
                    target="_blank"
                    className="flex items-center gap-2 text-green-600 hover:text-green-800 bg-white rounded-lg p-3 border"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Comparateur officiel prix carburants
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 5.2 Jump starter */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-yellow-500 rounded-xl flex items-center justify-center">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  🔋 Batterie HS : acheter un jump starter ou remplacer soi-même
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">30 min</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">100 à 200 € par panne évitée</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Une panne de batterie est l'un des problèmes les plus courants. 
                  Beaucoup appellent un dépanneur et paient 100 à 200 € pour une intervention qui aurait pu être évitée.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">👉 Deux solutions simples et économiques :</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h5 className="font-bold text-blue-800 mb-3">Acheter un jump starter (50-80 €)</h5>
                    <p className="text-blue-700 text-sm mb-4">
                      Une batterie externe avec des pinces compacte qui permet de démarrer votre voiture seul.
                    </p>
                    <a 
                      href="https://amzn.to/4232J1B"
                      target="_blank"
                      className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors w-full justify-center"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Jump starter testé et approuvé SAVEA
                    </a>
                    <div className="text-blue-600 text-xs mt-2 text-center">
                      (fait aussi batterie externe et diagnostique)
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h5 className="font-bold text-green-800 mb-3">Changer la batterie soi-même</h5>
                    <p className="text-green-700 text-sm mb-4">
                      Souvent très simple, une batterie neuve coûte 60-120 € vs 200€ de dépannage.
                    </p>
                    <a 
                      href="https://amzn.to/4nIAUUx"
                      target="_blank"
                      className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors w-full justify-center"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Batterie livraison rapide
                    </a>
                    <div className="text-green-600 text-xs mt-2 text-center">
                      (attention start & stop si votre voiture a cette fonction)
                    </div>
                  </div>
                </div>

                <div className="bg-blue-100 rounded-lg p-4 mt-4">
                  <p className="text-blue-800 text-center font-medium">
                    💡 <strong>Investissement unique qui se rentabilise dès la première panne évitée.</strong>
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 5.3 Apps prix carburant */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  📱 Applications pour comparer les prix du carburant
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Très facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">Immédiat</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">50 à 150 €/an</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Le prix du carburant peut varier de 10 à 20 centimes/L entre deux stations d'une même ville. 
                  Sur un plein de 50 L, ça fait 5 à 10 € d'écart, soit 200 à 300 € par an pour un gros rouleur.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">📱 Applications testées SAVEA :</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <a href="https://apps.apple.com/es/app/essence-co/id459724569" target="_blank" className="bg-white rounded-lg p-4 border hover:shadow-md transition-shadow">
                    <div className="text-center">
                      <div className="font-bold text-sage-900 mb-1">Essence & Co</div>
                      <div className="text-sage-600 text-xs mb-2">8 millions utilisateurs France</div>
                      <div className="text-green-600 font-semibold text-sm">Top choix SAVEA</div>
                    </div>
                  </a>

                  <a href="https://www.prix-carburants.gouv.fr/" target="_blank" className="bg-white rounded-lg p-4 border hover:shadow-md transition-shadow">
                    <div className="text-center">
                      <div className="font-bold text-sage-900 mb-1">Prix Carburants</div>
                      <div className="text-sage-600 text-xs mb-2">Site officiel gouvernement</div>
                      <div className="text-blue-600 font-semibold text-sm">100% officiel</div>
                    </div>
                  </a>

                  <a href="https://apps.apple.com/us/app/gasbuddy-find-pay-for-gas/id406719683" target="_blank" className="bg-white rounded-lg p-4 border hover:shadow-md transition-shadow">
                    <div className="text-center">
                      <div className="font-bold text-sage-900 mb-1">GasBuddy</div>
                      <div className="text-sage-600 text-xs mb-2">Pratique à l'étranger</div>
                      <div className="text-purple-600 font-semibold text-sm">International</div>
                    </div>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Conclusion Chapitre 5 */}
          <Card className="bg-gradient-to-r from-red-50 to-rose-50 border-red-300 border-2">
            <CardContent className="p-8">
              <h3 className="font-bold text-red-800 text-2xl mb-4">✅ Conclusion Chapitre 5</h3>
              <p className="text-red-700 text-lg leading-relaxed">
                Avec l'E85, la gestion maline de votre batterie et le choix de la station la moins chère, 
                vous pouvez économiser <strong>800 à 1 200 €/an</strong> sans changer vos habitudes de conduite. 
                C'est surprenant que peu de gens le fassent encore : c'est légal, accessible, et très rentable.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CHAPITRE 6 COMPLET - Transports & voyages */}
      <section id="chapitre-6" className="py-20 bg-gradient-to-br from-indigo-50 to-purple-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-indigo-900 mb-4">
              ✈️ Chapitre 6 – Transports & voyages
            </h2>
            <div className="text-sm text-indigo-600 mb-4">
              📅 Publié par SAVEA - Avril 2026
            </div>
            <p className="text-xl text-indigo-700">
              Voyager malin et économique en France et Europe
            </p>
          </div>

          {/* 6.1 Train SNCF */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                    <Plane className="w-6 h-6 text-white" />
                  </div>
                  🚆 Voyager en train : le confort malin
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">30min-1h</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">200 à 600 €/an</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Nos équipes privilégient toujours le train pour les déplacements en France et en Europe. 
                  Contrairement à ce qu'on croit, porte-à-porte, le train n'est pas forcément plus long que l'avion :
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
                <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                  <h4 className="font-bold text-red-800 text-lg mb-3">✈️ En avion</h4>
                  <p className="text-red-700 text-sm">
                    On perd beaucoup de temps dans les transports vers les aéroports, les contrôles, 
                    et l'attente debout sans rien pouvoir faire.
                  </p>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                  <h4 className="font-bold text-green-800 text-lg mb-3">🚂 En train</h4>
                  <p className="text-green-700 text-sm">
                    Tout le temps passé est du temps utile : vous pouvez lire, travailler, dormir, 
                    ou simplement profiter du voyage.
                  </p>
                </div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">💡 Les cartes SNCF sont indispensables - À acheter au Black Friday</h4>
                
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <h5 className="font-bold text-blue-800 text-xl mb-4">Carte Avantage (Jeune, Adulte, Senior)</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                      <div className="text-blue-700">
                        <strong>Prix normal :</strong> 49 €/an
                      </div>
                      <div className="text-green-700">
                        <strong>Black Friday :</strong> -50 % = 25 € seulement !
                      </div>
                    </div>
                    <div className="space-y-2">
                      <h6 className="font-bold text-blue-800">Elle donne :</h6>
                      <ul className="text-blue-700 text-sm space-y-1 list-disc pl-4">
                        <li><strong>-30 % minimum</strong> sur billets TGV et Intercités</li>
                        <li><strong>Prix plafonnés</strong> par trajet</li>
                        <li><strong>-15%</strong> sur la restauration en TGV (personne n'y pense !)</li>
                      </ul>
                    </div>
                  </div>
                  <div className="bg-green-100 rounded-lg p-4 mt-4 text-center">
                    <p className="text-green-800 font-bold">
                      Achetée au Black Friday (et même en temps normal), cette carte se rentabilise en un aller retour.
                    </p>
                  </div>
                  <div className="text-center mt-4">
                    <a 
                      href="https://www.sncf-voyageurs.com/fr/voyagez-avec-nous/en-france/tarifs-grandes-lignes/carte-avantage/carte-avantage-adulte/"
                      target="_blank"
                      className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Carte Avantage SNCF
                    </a>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <h4 className="font-bold text-green-800 text-lg mb-4">💡 Astuces pratiques SNCF testées SAVEA</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <h5 className="font-bold text-green-800 mb-2">⏳ Option blocage de prix</h5>
                      <p className="text-green-700 text-sm">
                        La SNCF propose de bloquer gratuitement un billet pendant 7 jours, sans payer. 
                        Idéal pour sécuriser un tarif intéressant le temps de réfléchir.
                      </p>
                    </div>

                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <h5 className="font-bold text-green-800 mb-2">💸 Annulation & remboursement</h5>
                      <p className="text-green-700 text-sm">
                        La plupart des billets sont 100 % remboursables jusqu'à 7 jours avant le départ. 
                        Conseil SAVEA : réservez tôt même si vous n'êtes pas sûr, et annulez plus tard si besoin.
                      </p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <h5 className="font-bold text-green-800 mb-2">⏱️ Astuce G30 (garantie retard)</h5>
                      <p className="text-green-700 text-sm">
                        Si votre train a plus de 30 minutes de retard, indemnisation automatique :
                        25 % du prix dès 30 minutes, jusqu'à 75 % au-delà de 3 heures. 
                        Très peu utilisent ce droit !
                      </p>
                    </div>

                    <div className="bg-white rounded-lg p-4 border border-gray-200">
                      <h5 className="font-bold text-green-800 mb-2">🎫 Billets plus longs</h5>
                      <p className="text-green-700 text-sm">
                        Algorithmes SNCF : un trajet plus long peut coûter moins cher. 
                        Exemple : Paris-Perpignan moins cher que Paris-Narbonne, alors que vous descendez avant.
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                <h4 className="font-bold text-purple-800 text-lg mb-4">🌙 Trains de nuit : un retour en force</h4>
                <p className="text-purple-700 leading-relaxed mb-4">
                  Les trains de nuit font leur grand retour, et c'est une excellente alternative à l'avion ou à l'hôtel. 
                  Exemple : Paris → Latour-de-Carol (Pyrénées) dès 19 € en saison creuse. 
                  Vous partez le vendredi soir, dormez tranquillement, et vous réveillez au cœur des montagnes le samedi matin. 
                  Résultat : un week-end entier sans payer d'hôtel inutile.
                </p>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <h5 className="font-bold text-purple-800 mb-3">👉 Lignes intéressantes testées :</h5>
                  <div className="grid grid-cols-2 gap-2 text-purple-700 text-sm">
                    <div>• Paris → Briançon (Alpes)</div>
                    <div>• Paris → Nice (Méditerranée)</div>
                    <div>• Paris → Tarbes/Lourdes (Pyrénées)</div>
                    <div>• Paris → Cerbère (frontière espagnole)</div>
                  </div>
                </div>
              </div>

              <div className="bg-indigo-50 border border-indigo-200 rounded-xl p-6">
                <h4 className="font-bold text-indigo-800 text-lg mb-4">🌍 Liaisons internationales testées</h4>
                <p className="text-indigo-700 leading-relaxed mb-4">
                  Le réseau ferroviaire européen est dense et souvent plus agréable que l'avion. 
                  Quelques exemples depuis la France :
                </p>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h5 className="font-bold text-indigo-800 mb-2">🇬🇧 Vers Royaume-Uni</h5>
                    <div className="text-indigo-700 text-sm space-y-1">
                      <div>• Paris – London (Eurostar 2h20)</div>
                      <div>• Paris – Bruxelles/Amsterdam</div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h5 className="font-bold text-indigo-800 mb-2">🇪🇸 Vers Espagne</h5>
                    <div className="text-indigo-700 text-sm space-y-1">
                      <div>• Paris – Barcelone (TGV 6h30)</div>
                      <div>• Paysages à couper le souffle</div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h5 className="font-bold text-indigo-800 mb-2">🇮🇹 Vers Italie</h5>
                    <div className="text-indigo-700 text-sm space-y-1">
                      <div>• Marseille – Milan/Turin</div>
                      <div>• Nice – Vintimille (Riviera)</div>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h5 className="font-bold text-indigo-800 mb-2">🇩🇪🇦🇹 Vers Europe</h5>
                    <div className="text-indigo-700 text-sm space-y-1">
                      <div>• Paris – Berlin/Vienne (Nightjet)</div>
                      <div>• Trains nuit autrichiens modernes</div>
                    </div>
                  </div>
                </div>

                <div className="bg-indigo-100 rounded-lg p-4 mt-4">
                  <p className="text-indigo-800 font-bold text-center">
                    👉 Résumé SAVEA : avec les cartes Avantage, les trains de nuit, les liaisons internationales et l'astuce G30, 
                    voyager en train peut devenir non seulement plus confortable que l'avion, mais aussi bien plus économique.
                  </p>
                </div>

                <div className="text-center mt-4">
                  <a 
                    href="https://www.sncf-connect.com/aide/retard-de-votre-train-et-remboursement"
                    target="_blank"
                    className="inline-flex items-center gap-2 bg-indigo-600 text-white px-4 py-2 rounded-lg hover:bg-indigo-700 transition-colors mr-3"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Programme G30
                  </a>
                  <a 
                    href="https://www.thetrainline.com/fr"
                    target="_blank"
                    className="inline-flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Comparateur Trainline
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 6.2 Avion malin */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center">
                    <PlaneIcon className="w-6 h-6 text-white" />
                  </div>
                  ✈️ Voyager en avion : dépenser moins sans se faire piéger
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">30 min</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">100 à 400 €/an</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Nos équipes sont moins fans de l'avion, mais il reste parfois indispensable. 
                  Le problème, c'est que les compagnies aériennes regorgent de pièges tarifaires. 
                  Voici comment les éviter :
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">🔍 Comparer intelligemment :</h4>
                <ul className="text-blue-700 space-y-2 list-disc pl-6 mb-4">
                  <li><strong>Utilisez toujours un comparateur</strong> comme Skyscanner</li>
                  <li><strong>Activez dates flexibles et aéroports à proximité :</strong> une journée d'écart peut diviser le prix par deux</li>
                  <li><strong>Attention cookies :</strong> ne revenez pas 10 fois sur le site de la compagnie, les prix grimpent</li>
                  <li><strong>Méthode SAVEA :</strong> recherches sur comparateur, puis réservation directe en une fois</li>
                </ul>

                <a 
                  href="https://www.skyscanner.fr/"
                  target="_blank"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Comparateur Skyscanner
                </a>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <h4 className="font-bold text-green-800 text-lg mb-4">✅ Choisir le bon billet :</h4>
                <ul className="text-green-700 space-y-2 list-disc pl-6 mb-4">
                  <li><strong>Prenez le billet nu,</strong> le moins cher possible sans toutes les options</li>
                  <li><strong>Ajoutez ensuite seulement</strong> ce dont vous avez vraiment besoin (ex. un bagage)</li>
                  <li><strong>Les "packs" avec embarquement prioritaire</strong> sont souvent du pur marketing</li>
                </ul>

                <div className="bg-green-100 rounded-lg p-4">
                  <p className="text-green-800 font-bold text-sm">
                    👉 <strong>Astuce avion SAVEA :</strong> inutile de payer pour réserver une place. 
                    Cliquez sur "continuer sans choisir de siège" : une place vous sera attribuée gratuitement à l'enregistrement.
                  </p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <h4 className="font-bold text-yellow-800 text-lg mb-4">💡 Les droits des passagers</h4>
                <ul className="text-yellow-700 space-y-2 list-disc pl-6 mb-4">
                  <li>Si votre vol est annulé ou fortement retardé, <strong>l'UE prévoit des indemnisations jusqu'à 600 €</strong></li>
                  <li><strong>Même avec un billet "non annulable"</strong>, vous pouvez toujours demander le remboursement des taxes aéroportuaires</li>
                  <li>C'est <strong>obligatoire, prévu par la loi</strong>, et souvent oublié par les passagers</li>
                </ul>

                <a 
                  href="https://www.service-public.fr/particuliers/vosdroits/N84"
                  target="_blank"
                  className="inline-flex items-center gap-2 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Vos droits en avion
                </a>
              </div>
            </CardContent>
          </Card>

          {/* 6.3 Hôtels Booking */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-pink-500 rounded-xl flex items-center justify-center">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                  🏨 Réserver ses hôtels : Booking.com, mais malin
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Très facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">15 min</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">100 à 300 €/an</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Nos équipes utilisent quasiment toujours Booking.com, et voici pourquoi et comment maximiser vos réductions :
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">💡 Méthode SAVEA pour maximiser réductions :</h4>
                <ul className="text-blue-700 space-y-2 list-disc pl-6 mb-4">
                  <li><strong>Toujours passer par l'appli mobile,</strong> connecté à votre compte</li>
                  <li><strong>Après quelques réservations,</strong> vous atteignez le statut "Genius" qui donne jusqu'à 30 % de réduction et des avantages (petit-déjeuner offert, surclassement, check-in prioritaire)</li>
                  <li><strong>La plupart des hôtels offrent l'annulation gratuite.</strong> Permet de réserver tôt, même sans certitude, et d'annuler sans frais si besoin</li>
                  <li><strong>Même pas besoin de carte de crédit</strong> pour réserver</li>
                </ul>
              </div>
            </CardContent>
          </Card>

          {/* 6.4 Location voiture */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center">
                    <Car className="w-6 h-6 text-white" />
                  </div>
                  🚗 Louer une voiture sans se ruiner
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">30 min</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">50 à 200 €/an</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  La location de voiture est un autre poste où l'on peut vite se faire avoir.
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <h4 className="font-bold text-blue-800 mb-3">🇫🇷 En France :</h4>
                  <p className="text-blue-700 text-sm leading-relaxed mb-4">
                    Nous recommandons Getaround, la location entre particuliers. 
                    C'est simple, rapide, et tout se fait via smartphone (clé digitale).
                  </p>
                  <a 
                    href="https://fr.getaround.com/"
                    target="_blank"
                    className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Getaround France
                  </a>
                </div>

                <div className="bg-orange-50 border border-orange-200 rounded-xl p-6">
                  <h4 className="font-bold text-orange-800 mb-3">🇪🇸 En Espagne :</h4>
                  <p className="text-orange-700 text-sm leading-relaxed">
                    Record Go fait partie des loueurs les plus compétitifs selon nos comparaisons.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 6.5 Assurances voyage doublons */}
          <Card className="mb-12">
            <CardHeader>
              <CardTitle className="flex items-center gap-3 text-xl">
                <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center">
                  <AlertTriangle className="w-6 h-6 text-white" />
                </div>
                ⚠️ Les assurances voyage : attention aux doublons
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Quand vous voyagez, on vous propose toujours des assurances supplémentaires : 
                  annulation, vol de bagages, assurance location voiture…
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <h4 className="font-bold text-yellow-800 text-lg mb-4">👉 Dans la majorité des cas, elles sont inutiles, car :</h4>
                <ul className="text-yellow-700 space-y-2 list-disc pl-6 mb-4">
                  <li><strong>La loi européenne vous protège déjà</strong> sur beaucoup de points (retards, annulations)</li>
                  <li><strong>Et surtout :</strong> la plupart des cartes bancaires (Visa Premier, Mastercard Gold, etc.) 
                      incluent déjà ces couvertures automatiquement</li>
                </ul>

                <div className="bg-yellow-100 rounded-lg p-4">
                  <p className="text-yellow-800 font-bold text-sm">
                    💡 <strong>Conseil SAVEA :</strong> Avant de payer une assurance en plus, 
                    vérifiez toujours ce que votre carte bancaire couvre déjà. 
                    Vous éviterez de payer deux fois pour le même service.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Conclusion Chapitre 6 */}
          <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-300 border-2">
            <CardContent className="p-8">
              <h3 className="font-bold text-indigo-800 text-2xl mb-4">✅ Conclusion Chapitre 6</h3>
              <p className="text-indigo-700 text-lg leading-relaxed mb-4">
                Voyager malin, c'est connaître les bons réflexes :
              </p>
              <ul className="text-indigo-700 space-y-1 list-disc pl-6 mb-4">
                <li><strong>Train :</strong> cartes Avantage, astuce G30, trains de nuit</li>
                <li><strong>Avion :</strong> comparer intelligemment, éviter options inutiles, demander ses droits</li>
                <li><strong>Hôtels :</strong> statuts Booking Genius et annulation gratuite</li>
                <li><strong>Voitures :</strong> louer chez les bons acteurs ou entre particuliers</li>
                <li><strong>Assurances :</strong> refuser les doublons, vérifier protections carte bancaire</li>
              </ul>
              <div className="bg-indigo-100 rounded-lg p-4 mt-4">
                <div className="text-center">
                  <div className="font-bold text-indigo-800 text-xl">
                    Résultat : vous voyagez plus confortablement, avec plus de liberté, et vous économisez facilement 500 à 1 000 €/an.
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CHAPITRE 7 COMPLET - Courses & achats */}
      <section id="chapitre-7" className="py-20 bg-gradient-to-br from-teal-50 to-green-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-teal-900 mb-4">
              🛒 Chapitre 7 – Courses & achats
            </h2>
            <div className="text-sm text-teal-600 mb-4">
              📅 Publié par SAVEA - Mai 2026
            </div>
            <p className="text-xl text-teal-700">
              Acheter malin et éviter les pièges de consommation
            </p>
          </div>

          {/* 7.1 Acheter d'occasion */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                    <ShoppingCart className="w-6 h-6 text-white" />
                  </div>
                  ♻️ Acheter d'occasion et reconditionné : le réflexe malin
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">10 min recherche</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">30 à 70 % par produit</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Acheter neuf est un réflexe… mais rarement le plus malin. Dès qu'un produit sort de son carton, 
                  il perd une partie de sa valeur, même s'il n'a jamais servi. Autant profiter de cette "décote" 
                  quand c'est déjà fait par quelqu'un d'autre.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">👉 Stratégies testées SAVEA :</h4>
                
                <div className="space-y-4">
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h5 className="font-bold text-blue-800 mb-2">📦 Sur Amazon :</h5>
                    <p className="text-blue-700 text-sm">
                      L'option "D'Occasion – Comme neuf" permet d'économiser 10 à 30 % pour le même produit 
                      (seul le carton est abîmé). Nos équipes utilisent systématiquement cette astuce.
                    </p>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h5 className="font-bold text-blue-800 mb-2">🏃‍♂️ Chez Decathlon :</h5>
                    <p className="text-blue-700 text-sm">
                      Certains articles sont reconditionnés : par exemple un kayak 2 places à 150 € au lieu de 300 €, 
                      contrôlé et garanti par l'enseigne.
                    </p>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h5 className="font-bold text-blue-800 mb-2">📱 Sur Back Market :</h5>
                    <p className="text-blue-700 text-sm">
                      Smartphones et ordinateurs sont garantis 12 mois. Leurs standards de qualité sont élevés. 
                      Leur SAV est réactif, et si un produit ne satisfait pas, remboursement ou remplacement.
                    </p>
                    <a 
                      href="https://www.backmarket.fr/fr-fr/quality/smartphones"
                      target="_blank"
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs mt-2"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Standards de qualité Back Market
                    </a>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h5 className="font-bold text-blue-800 mb-2">🛍️ Sur Leboncoin :</h5>
                    <p className="text-blue-700 text-sm">
                      Filtrez "Comme neuf". Beaucoup de produits sont encore sous garantie, 
                      parfois même jamais ouverts (notamment après Noël).
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
                  <a href="https://www.amazon.fr/" target="_blank" className="bg-white rounded-lg p-3 border hover:shadow-md transition-shadow text-center">
                    <div className="font-medium text-sage-900 text-sm">Amazon</div>
                    <div className="text-sage-600 text-xs">Seconde main</div>
                  </a>
                  <a href="https://www.decathlon.fr/produits-reconditionnes" target="_blank" className="bg-white rounded-lg p-3 border hover:shadow-md transition-shadow text-center">
                    <div className="font-medium text-sage-900 text-sm">Decathlon</div>
                    <div className="text-sage-600 text-xs">Reconditionné</div>
                  </a>
                  <a href="https://www.backmarket.fr/" target="_blank" className="bg-white rounded-lg p-3 border hover:shadow-md transition-shadow text-center">
                    <div className="font-medium text-sage-900 text-sm">Back Market</div>
                    <div className="text-sage-600 text-xs">Tech garanti</div>
                  </a>
                  <a href="https://www.leboncoin.fr/" target="_blank" className="bg-white rounded-lg p-3 border hover:shadow-md transition-shadow text-center">
                    <div className="font-medium text-sage-900 text-sm">LeBonCoin</div>
                    <div className="text-sage-600 text-xs">Filtre "Comme neuf"</div>
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 7.2 Livres, vêtements & objets */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                    <BookOpen className="w-6 h-6 text-white" />
                  </div>
                  📚 Livres, vêtements & objets du quotidien
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Très facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">Variable</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">jusqu'à 80 %</div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <h4 className="font-bold text-blue-800 text-lg mb-3">📚 Livres :</h4>
                  <p className="text-blue-700 text-sm leading-relaxed mb-4">
                    Inutile d'acheter neuf. Sur Livrenpoche, on trouve des milliers de titres entre 2 et 5 €. 
                    Mais surtout : c'est une coopérative française, une entreprise d'insertion à but non lucratif 
                    qui sauve des livres voués à la destruction et leur donne une seconde vie. 
                    Acheter chez eux, c'est à la fois faire des économies et soutenir une démarche sociale et écologique.
                  </p>
                  <a 
                    href="https://www.livrenpoche.com/"
                    target="_blank"
                    className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors w-full justify-center"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Livrenpoche
                  </a>
                </div>

                <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                  <h4 className="font-bold text-green-800 text-lg mb-3">👕 Vêtements :</h4>
                  <p className="text-green-700 text-sm leading-relaxed mb-4">
                    Emmaüs et la Croix-Rouge restent des mines d'or. Derrière chaque achat, 
                    vous soutenez des associations qui financent l'aide aux plus démunis 
                    et vous pouvez trouver des manteaux de marque pour quelques euros.
                  </p>
                  <div className="space-y-2">
                    <a 
                      href="https://emmaus-france.org/acheter/"
                      target="_blank"
                      className="flex items-center gap-2 text-green-600 hover:text-green-800 text-sm"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Localiser magasins Emmaüs
                    </a>
                    <a 
                      href="https://www.croix-rouge.fr/annuaire?categories=boutique-solidaire"
                      target="_blank"
                      className="flex items-center gap-2 text-green-600 hover:text-green-800 text-sm"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Localiser magasins Croix-Rouge
                    </a>
                  </div>
                </div>

                <div className="bg-purple-50 border border-purple-200 rounded-xl p-6">
                  <h4 className="font-bold text-purple-800 text-lg mb-3">🏠 Objets quotidien :</h4>
                  <p className="text-purple-700 text-sm leading-relaxed mb-4">
                    Sur Leboncoin ou Vinted, on trouve du neuf jamais utilisé ou presque. 
                    Mais l'astuce la plus rentable est aussi de vendre tout ce qui dort chez vous. 
                    Un vieil appareil photo, une console inutilisée, des meubles qui prennent la poussière… 
                    tout peut partir rapidement, et ça fait rentrer du cash sans aucun effort.
                  </p>
                  <a 
                    href="https://www.vinted.fr/"
                    target="_blank"
                    className="inline-flex items-center gap-2 bg-purple-600 text-white px-4 py-2 rounded-lg hover:bg-purple-700 transition-colors w-full justify-center"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Vinted
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 7.3 Courses supermarché */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                    <TrendingDown className="w-6 h-6 text-white" />
                  </div>
                  🛒 Courses au supermarché : déjouer la shrinkflation
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">15-30 min/course</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">20 à 30 % sur le budget alimentaire</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Les industriels pratiquent la shrinkflation : même prix, moins de produit. 
                  Exemple : un paquet de biscuits passe de 200 g à 170 g pour le même prix → 
                  en réalité, +17 % d'augmentation cachée.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">👉 Réflexes à adopter :</h4>
                <ul className="text-blue-700 space-y-2 list-disc pl-6 mb-4">
                  <li><strong>Toujours regarder le prix au kilo ou au litre</strong></li>
                  <li><strong>Favoriser le vrac :</strong> riz, pâtes, fruits secs → -30 % en moyenne</li>
                  <li><strong>Acheter en grands formats :</strong> lessive 5 L coûte 25 % moins que petits flacons</li>
                  <li><strong>Formats XXL :</strong> 25L de lessive, plus économique et écologique</li>
                </ul>

                <a 
                  href="https://www.quechoisir.org/carte-interactive-drives-n21243/"
                  target="_blank"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Carte UFC-Que Choisir comparateur drives
                </a>
              </div>
            </CardContent>
          </Card>

          {/* 7.4 Produits techno */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  🧰 Produits technologiques et de maison : éviter les mauvais choix
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">20 min recherche</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">10 à 50 %</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Un mauvais achat technique, c'est un double gaspillage : argent et produit.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">👉 Réflexe SAVEA : consulter Wirecutter</h4>
                <p className="text-blue-700 leading-relaxed mb-4">
                  Le site de tests produit du New York Times. Ils passent des dizaines d'heures sur chaque comparatif 
                  pour vous donner :
                </p>
                <ul className="text-blue-700 space-y-1 list-disc pl-6 mb-4">
                  <li>le meilleur produit</li>
                  <li>une alternative plus économique</li>
                  <li>une version haut de gamme</li>
                </ul>

                <div className="bg-white rounded-lg p-4 border border-gray-200">
                  <h5 className="font-bold text-blue-800 mb-2">💡 Exemple testé SAVEA :</h5>
                  <p className="text-blue-700 text-sm mb-3">
                    Sur leur comparatif des cafetières filtre après 120 heures de tests, 
                    la OXO Brew 8-Cup arrive en tête, mais une option plus abordable (Ninja CE251) 
                    est recommandée comme "budget choice".
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                    <a 
                      href="https://www.nytimes.com/wirecutter/"
                      target="_blank"
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Site Wirecutter
                    </a>
                    <a 
                      href="https://www.nytimes.com/wirecutter/reviews/best-coffee-maker/"
                      target="_blank"
                      className="flex items-center gap-1 text-blue-600 hover:text-blue-800 text-sm"
                    >
                      <ExternalLink className="w-3 h-3" />
                      Exemple cafetières
                    </a>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 7.5 Codes promo & traque prix */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-pink-500 rounded-xl flex items-center justify-center">
                    <Gift className="w-6 h-6 text-white" />
                  </div>
                  💻 Codes promo & traque des prix
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Très facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">5 min</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">5 à 20 % par achat</div>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">👉 Avant chaque achat en ligne :</h4>
                <ul className="text-blue-700 space-y-3 list-disc pl-6">
                  <li>
                    <strong>Recherchez codes promos :</strong> tapez simplement dans Google "Code Promo + votre site". 
                    Il est fort probable qu'un code de quelques % soit disponible. 
                    Sinon, utilisez Honey : l'extension Chrome qui teste puis applique automatiquement les coupons disponibles.
                  </li>
                  <li>
                    <strong>Inscrivez-vous à la newsletter</strong> du site en question : la plupart offrent -10 % sur le premier achat. 
                    Pensez à vous désabonner directement une fois le code reçu.
                  </li>
                  <li>
                    <strong>Vérifiez l'historique des prix Amazon</strong> avec Keepa et CamelCamelCamel
                  </li>
                </ul>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <h4 className="font-bold text-yellow-800 text-lg mb-4">📊 Outils de traque prix testés SAVEA :</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h5 className="font-bold text-yellow-800 mb-2">Keepa</h5>
                    <p className="text-yellow-700 text-sm mb-3">
                      Extension qui affiche directement l'évolution du prix dans Amazon en temps réel.
                    </p>
                    <div className="space-y-2">
                      <a 
                        href="https://keepa.com/#!search"
                        target="_blank"
                        className="block text-yellow-600 hover:text-yellow-800 text-xs"
                      >
                        <ExternalLink className="w-3 h-3 inline mr-1" />
                        Site Keepa (coller lien produit)
                      </a>
                      <a 
                        href="https://chromewebstore.google.com/detail/keepa-amazon-price-tracke/neebplgakaahbhdphmkckjjcegoiijjo"
                        target="_blank"
                        className="block text-yellow-600 hover:text-yellow-800 text-xs"
                      >
                        <ExternalLink className="w-3 h-3 inline mr-1" />
                        Extension Chrome
                      </a>
                    </div>
                  </div>

                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <h5 className="font-bold text-yellow-800 mb-2">CamelCamelCamel</h5>
                    <p className="text-yellow-700 text-sm mb-3">
                      Site qui trace les prix sur plusieurs années, et vous alerte quand le produit passe sous votre seuil.
                    </p>
                    <a 
                      href="https://fr.camelcamelcamel.com/"
                      target="_blank"
                      className="text-yellow-600 hover:text-yellow-800 text-xs"
                    >
                      <ExternalLink className="w-3 h-3 inline mr-1" />
                      CamelCamelCamel
                    </a>
                  </div>
                </div>

                <div className="bg-yellow-100 rounded-lg p-4">
                  <h5 className="font-bold text-yellow-800 text-sm mb-2">📊 Exemple concret testé SAVEA :</h5>
                  <p className="text-yellow-700 text-sm">
                    Smartphone affiché "-25 %" sur Amazon. Keepa montre qu'il a déjà été vendu à ce prix le mois dernier : promo bidon. 
                    CamelCamelCamel vous enverra une alerte quand il descendra vraiment.
                  </p>
                </div>

                <div className="text-center mt-4">
                  <a 
                    href="https://chromewebstore.google.com/detail/honey-automatic-coupons-r/bmnlcjabgnpnenekpadlanbbkooimhnj"
                    target="_blank"
                    className="inline-flex items-center gap-2 bg-orange-600 text-white px-4 py-2 rounded-lg hover:bg-orange-700 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Extension Honey (codes promo auto)
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Conclusion Chapitre 7 */}
          <Card className="bg-gradient-to-r from-teal-50 to-green-50 border-teal-300 border-2">
            <CardContent className="p-8">
              <h3 className="font-bold text-teal-800 text-2xl mb-4">✅ Conclusion Chapitre 7</h3>
              <p className="text-teal-700 text-lg leading-relaxed">
                Acheter malin, ce n'est pas se priver : c'est éviter de se faire avoir. 
                Acheter d'occasion ou reconditionné, privilégier le social et le solidaire, vendre ce qui ne sert plus, 
                traquer les prix au kilo et éviter les fausses promos : tout cela, ce sont des économies immédiates. 
                <strong>Résultat : plusieurs centaines d'euros par an en plus dans votre poche… et une consommation plus écologique et solidaire.</strong>
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CHAPITRE 8 COMPLET - Abonnements & divertissement */}
      <section id="chapitre-8" className="py-20 bg-gradient-to-br from-pink-50 to-rose-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-pink-900 mb-4">
              📺 Chapitre 8 – Abonnements & divertissement
            </h2>
            <div className="text-sm text-pink-600 mb-4">
              📅 Publié par SAVEA - Juin 2026
            </div>
            <p className="text-xl text-pink-700">
              Profiter du divertissement sans se ruiner
            </p>
          </div>

          {/* 8.1 Essais gratuits */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                    <Gift className="w-6 h-6 text-white" />
                  </div>
                  🎁 Profiter des essais gratuits sans crainte
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Très facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">5 min</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">50 à 300 €/an selon services testés</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Nous le disons franchement : il ne faut jamais avoir peur de profiter des essais gratuits. 
                  Les plateformes (Netflix, Amazon Prime, Apple TV, Spotify, etc.) vivent du fait que la majorité 
                  des gens oublient d'annuler et continuent de payer.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">👉 Mais la loi est claire :</h4>
                <p className="text-blue-700 leading-relaxed mb-4">
                  Vous pouvez souscrire à un essai gratuit, l'annuler immédiatement et profiter quand même 
                  de la totalité de la période offerte. Même si le site vous laisse entendre que vous perdez 
                  l'accès en cas d'annulation, c'est faux. Nos équipes ont testé des dizaines de fois, 
                  et vous gardez bien l'accès jusqu'au dernier jour.
                </p>

                <div className="bg-blue-100 rounded-lg p-4">
                  <h5 className="font-bold text-blue-800 text-sm mb-2">💡 Conseil SAVEA :</h5>
                  <p className="text-blue-700 text-sm">
                    À chaque fois que nos équipes s'inscrivent à un essai gratuit, nous annulons dans les 5 minutes. 
                    De cette façon, pas de risque d'oubli, et vous profitez tranquillement du service pendant tout le mois offert.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 8.2 Spliiit */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-cyan-500 rounded-xl flex items-center justify-center">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  🔄 Spliiit : partager ses abonnements
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Très facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">5 min inscription</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">40 à 70 % sur vos abonnements</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Le site internet Spliiit permet de partager légalement vos abonnements multi-utilisateurs 
                  (Netflix, Spotify, Disney+, VPN, etc.) et de diviser la facture avec d'autres personnes.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">📊 Exemple testé SAVEA :</h4>
                <p className="text-blue-700 leading-relaxed mb-4">
                  Un Netflix à 16 €/mois avec 4 écrans revient à environ 4 €/mois par personne. 
                  Chaque utilisateur garde son propre profil, et vous pouvez rejoindre ou quitter un partage quand vous voulez. 
                  Nos équipes ont testé sur Spotify et YouTube Premium : hyper simple, fiable, et beaucoup moins cher.
                </p>

                <a 
                  href="https://www.spliiit.com"
                  target="_blank"
                  className="inline-flex items-center gap-2 bg-cyan-600 text-white px-4 py-2 rounded-lg hover:bg-cyan-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Site Spliiit
                </a>
              </div>
            </CardContent>
          </Card>

          {/* 8.3 Canal+ */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-red-500 rounded-xl flex items-center justify-center">
                    <Tv className="w-6 h-6 text-white" />
                  </div>
                  🎬 Le pack Canal+ CinéSérie Plus : le meilleur combo
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">15 min</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">100 à 200 €/an vs abonnements séparés</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Si vous aimez les films et les séries, nous recommandons le pack Canal+ CinéSérie Plus. 
                  Au lieu de payer séparément Netflix, Paramount+, OCS et Canal+, ce pack regroupe tout 
                  pour un prix unique bien plus bas.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">💰 Comparaison SAVEA :</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 border border-red-300 text-center">
                    <h5 className="font-bold text-red-800 mb-2">Abonnements séparés</h5>
                    <div className="text-red-700">50 à 70 € par mois</div>
                  </div>
                  <div className="bg-white rounded-lg p-4 border border-green-300 text-center">
                    <h5 className="font-bold text-green-800 mb-2">Pack CinéSérie Plus</h5>
                    <div className="text-green-700">~35 € par mois</div>
                  </div>
                </div>
                
                <div className="bg-green-100 rounded-lg p-4 mt-4">
                  <p className="text-green-800 font-medium text-center">
                    💰 Économie : 100 à 200 €/an
                  </p>
                </div>

                <div className="bg-blue-100 rounded-lg p-4 mt-4">
                  <p className="text-blue-800 text-sm">
                    💡 <strong>Astuce SAVEA :</strong> Canal+ fait souvent des promos, par exemple -50 % la première année. 
                    Si vous voyez passer une offre comme ça, foncez.
                  </p>
                </div>

                <div className="text-center mt-4">
                  <a 
                    href="https://boutique.canalplus.com/produits/offre-canal-plus-cine-series"
                    target="_blank"
                    className="inline-flex items-center gap-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Offre Canal+ CinéSéries
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 8.4 Délais rétractation */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
                    <Shield className="w-6 h-6 text-white" />
                  </div>
                  🧩 Délais de rétractation : un droit que peu utilisent
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">10 min</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">quelques dizaines à centaines d'euros par an</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Un autre point très peu connu : <strong>la loi vous donne 14 jours de délai de rétractation 
                  pour tout achat en ligne.</strong> Et très peu de consommateurs l'utilisent.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">📦 Exemple concret :</h4>
                <p className="text-blue-700 leading-relaxed mb-4">
                  Vous commandez un set LEGO, vous changez d'avis, vous pensez être coincé… 
                  Eh bien non : vous avez 14 jours pour renvoyer le produit et être intégralement remboursé. 
                  Et ça vaut surtout pour énormément d'abonnements souscrits en ligne (presse, jeux vidéo, streaming).
                </p>
                <p className="text-blue-700 text-sm">
                  Nos équipes l'ont utilisé plusieurs fois pour éviter des achats impulsifs qui auraient fini 
                  par prendre la poussière ou des abonnements annuels oubliés.
                </p>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
                <h4 className="font-bold text-yellow-800 text-lg mb-4">💡 Astuce pratique SAVEA :</h4>
                <p className="text-yellow-700 leading-relaxed">
                  Si une entreprise demande une lettre recommandée pour annuler, inutile de vous compliquer la vie. 
                  Il existe des sites comme lettre-resiliation.com : vous remplissez un formulaire, 
                  vous payez 2 €, et ils s'occupent d'imprimer et d'envoyer la lettre à votre place. Simple, efficace, et légal.
                </p>
                <div className="text-center mt-4">
                  <a 
                    href="https://www.lettre-resiliation.com/"
                    target="_blank"
                    className="inline-flex items-center gap-2 bg-yellow-600 text-white px-4 py-2 rounded-lg hover:bg-yellow-700 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    lettre-resiliation.com
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 8.5 Stremio */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                    <Tv className="w-6 h-6 text-white" />
                  </div>
                  📺 Stremio : centraliser tous vos contenus
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-orange-100 text-orange-800 px-3 py-1">Moyen</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">15 min config</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">Accès gratuit à contenus divers</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Nous voudrions aussi vous parler d'une application qui change la donne : Stremio. 
                  Elle est totalement légale, disponible sur l'App Store, Google Play, ordinateur et smart TV.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">👉 L'idée est simple :</h4>
                <p className="text-blue-700 leading-relaxed mb-4">
                  Stremio permet de centraliser toutes vos sources de contenu dans une seule interface. 
                  Vous pouvez connecter YouTube, Twitch, services gratuits de films et séries… et même ajouter des extensions.
                </p>
                <p className="text-blue-700 text-sm mb-4">
                  Certaines extensions, comme Torrentio, donnent accès à quasiment tous les contenus imaginables. 
                  Nous conseillons cette vidéo d'1 minute qui explique très bien le principe.
                </p>

                <a 
                  href="https://www.youtube.com/watch?v=DyzsZehINaU"
                  target="_blank"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Vidéo explicative (1 min)
                </a>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                <h4 className="font-bold text-red-800 text-lg mb-4">⚠️ Disclaimer SAVEA :</h4>
                <p className="text-red-700 text-sm leading-relaxed">
                  Stremio en soi est légal. Mais certaines extensions tierces permettent d'accéder 
                  à des contenus protégés par droits d'auteur gratuitement. 
                  <strong>Utilisez donc Stremio avec prudence, et respectez toujours la législation locale.</strong>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Conclusion Chapitre 8 */}
          <Card className="bg-gradient-to-r from-pink-50 to-rose-50 border-pink-300 border-2">
            <CardContent className="p-8">
              <h3 className="font-bold text-pink-800 text-2xl mb-4">✅ Conclusion Chapitre 8</h3>
              <p className="text-pink-700 text-lg leading-relaxed">
                Ne pas profiter des essais gratuits, c'est laisser de l'argent sur la table. 
                Avec les lois actuelles, il est facile de tester, d'annuler immédiatement et de garder l'accès jusqu'à la fin. 
                Pour les amateurs de séries et de cinéma, Canal+ CinéSérie Plus reste le meilleur combo qualité/prix. 
                Ajoutez à cela vos droits au délai de rétractation, et des applis malines comme Stremio, 
                et vous avez toutes les cartes en main pour profiter du divertissement sans vous ruiner.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* CHAPITRE 9 COMPLET - Bonus hacks */}
      <section id="chapitre-9" className="py-20 bg-gradient-to-br from-amber-50 to-yellow-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-amber-900 mb-4">
              🎁 Chapitre 9 – Bonus hacks
            </h2>
            <div className="text-sm text-amber-600 mb-4">
              📅 Publié par SAVEA - Juillet 2026
            </div>
            <p className="text-xl text-amber-700">
              Les astuces bonus qui changent la vie quotidienne
            </p>
          </div>

          {/* 9.1 Roborock */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-purple-500 rounded-xl flex items-center justify-center">
                    <Gift className="w-6 h-6 text-white" />
                  </div>
                  🤖 Roborock : dire adieu au ménage
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Très facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">15 min config</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">plusieurs heures/semaine ou équivalent femme de ménage (500-1000€/an)</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Nous l'avouons : ce n'est pas une "économie" au sens strict, c'est un petit caprice technologique. 
                  Mais un caprice qui change la vie.
                </p>
                <p className="text-sage-700 text-lg leading-relaxed">
                  Si votre logement est de plain-pied ou facilement accessible (sans trop d'escaliers), 
                  un Roborock évite littéralement de passer l'aspirateur et la serpillière. 
                  Nos équipes l'ont testé : 3 ans sans passer l'aspirateur. 
                  Nous avons passé des heures à comparer tous les modèles.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">📌 Modèles recommandés SAVEA :</h4>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-lg p-6 border border-gray-200">
                    <h5 className="font-bold text-blue-800 text-xl mb-3">Roborock Q10 S5</h5>
                    <p className="text-blue-700 text-sm mb-4">
                      Excellent pour l'aspiration, pas vraiment pour la serpillère honnêtement. 
                      Testé et validé par nos équipes sur plusieurs logements.
                    </p>
                    <a 
                      href="https://amzn.to/3W2ZUdg"
                      target="_blank"
                      className="inline-flex items-center gap-2 bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors w-full justify-center"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Roborock Q10 S5 testé SAVEA
                    </a>
                  </div>

                  <div className="bg-white rounded-lg p-6 border border-gray-200">
                    <h5 className="font-bold text-green-800 text-xl mb-3">Roborock Q Revo</h5>
                    <p className="text-green-700 text-sm mb-4">
                      Parfait si vous voulez aussi une serpillière de qualité. 
                      Son système de lavage automatique est bluffant selon nos tests.
                    </p>
                    <a 
                      href="https://amzn.to/4mrNtm9"
                      target="_blank"
                      className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors w-full justify-center"
                    >
                      <ExternalLink className="w-4 h-4" />
                      Roborock Q Revo testé SAVEA
                    </a>
                  </div>
                </div>

                <div className="bg-blue-100 rounded-lg p-4 mt-4">
                  <p className="text-blue-800 font-medium text-center">
                    💡 <strong>Résultat :</strong> fini les corvées de sol. Et si vous comparez avec le prix 
                    d'une femme de ménage régulière, l'investissement est vite rentabilisé.
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* 9.2 Park4Night */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                    <Car className="w-6 h-6 text-white" />
                  </div>
                  🏕️ Camper gratuitement avec Park4Night
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">10 min recherche</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">20 à 80 €/nuit d'hôtel ou camping</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Envie de vous dépayser sans dépenser un centime en hébergement ? 
                  L'application Park4Night recense des milliers d'endroits où camper gratuitement 
                  en van, camping-car ou même en tente. Parkings nature, bords de lac, aires gratuites : 
                  il suffit de choisir et d'y passer la nuit.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <p className="text-blue-700 leading-relaxed mb-4">
                  C'est une astuce qui a sauvé bien des week-ends improvisés de nos utilisateurs, surtout en été. 
                  Et même si vous ne voyagez pas souvent, l'appli vaut largement le coup d'œil 
                  et son abonnement annuel est vraiment raisonnable.
                </p>

                <a 
                  href="https://park4night.com/fr"
                  target="_blank"
                  className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                >
                  <ExternalLink className="w-4 h-4" />
                  Park4Night
                </a>
              </div>
            </CardContent>
          </Card>

          {/* 9.3 Joko Cashback */}
          <Card className="mb-12">
            <CardHeader>
              <div className="flex items-center justify-between flex-wrap gap-4">
                <CardTitle className="flex items-center gap-3 text-xl">
                  <div className="w-12 h-12 bg-green-500 rounded-xl flex items-center justify-center">
                    <TrendingDown className="w-6 h-6 text-white" />
                  </div>
                  💸 Cashback avec Joko
                </CardTitle>
                <div className="flex gap-2">
                  <Badge className="bg-green-100 text-green-800 px-3 py-1">Facile</Badge>
                  <Badge className="bg-blue-100 text-blue-800 px-3 py-1">10 min setup</Badge>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-xl p-6 text-center">
                <div className="text-3xl font-bold text-green-700 mb-2">💰 Économie estimée</div>
                <div className="text-2xl font-bold text-green-800">5 à 10 % sur vos achats, soit 50 à 200 €/an</div>
              </div>

              <div className="prose prose-sage max-w-none">
                <p className="text-sage-700 text-lg leading-relaxed">
                  Le cashback, c'est de l'argent que vous récupérez sur vos achats, et Joko est l'une des applis 
                  les plus simples en France. Vous reliez votre carte bancaire, vous continuez vos achats normalement 
                  (Amazon, Carrefour, Fnac, etc.), et Joko vous reverse automatiquement une partie de vos dépenses.
                </p>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-800 text-lg mb-4">📊 Exemple testé SAVEA :</h4>
                <p className="text-blue-700 leading-relaxed mb-4">
                  3 % remboursés sur un panier de 100 € = 3 € qui reviennent directement sur votre compte. 
                  Ça paraît petit, mais cumulé sur l'année, c'est très intéressant.
                </p>
                <div className="bg-blue-100 rounded-lg p-4">
                  <p className="text-blue-800 text-sm">
                    💡 <strong>Bonus :</strong> vous pouvez convertir vos gains en virements bancaires ou cartes cadeaux.
                  </p>
                </div>

                <div className="text-center mt-4">
                  <a 
                    href="https://www.joko.com/fr"
                    target="_blank"
                    className="inline-flex items-center gap-2 bg-green-600 text-white px-4 py-2 rounded-lg hover:bg-green-700 transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    Joko Cashback
                  </a>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Conclusion Chapitre 9 */}
          <Card className="bg-gradient-to-r from-amber-50 to-yellow-50 border-amber-300 border-2">
            <CardContent className="p-8">
              <h3 className="font-bold text-amber-800 text-2xl mb-4">✅ Conclusion Chapitre 9</h3>
              <p className="text-amber-700 text-lg leading-relaxed">
                Ces petits hacks sont les "cerises sur le gâteau" : un Roborock qui transforme votre quotidien, 
                des nuits gratuites avec Park4Night, et du cashback automatique avec Joko. 
                Pas indispensables, mais de vrais bonus qui rendent la vie plus agréable… 
                tout en allégeant discrètement votre budget.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Conclusion finale guide */}
      <section className="py-16 bg-gradient-to-r from-sage-600 to-sage-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold text-white mb-6">🎯 Conclusion Finale</h2>
          <p className="text-sage-100 text-lg leading-relaxed mb-6">
            Ce guide n'a pas été écrit pour vous apprendre à vous priver, mais pour vous montrer qu'il existe 
            des dizaines d'astuces simples, légales et efficaces pour arrêter de gaspiller votre argent 
            là où ce n'est pas nécessaire.
          </p>
          <p className="text-sage-100 leading-relaxed mb-8">
            Changer de fournisseur d'énergie, payer son internet moins cher, éviter les fausses promos, 
            acheter malin, profiter des essais gratuits, voyager intelligemment… Chaque petit geste compte, 
            et c'est en les cumulant que vous ferez la différence.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 lg:gap-4 justify-center mb-8">
            <Button 
              onClick={() => window.open(`tel:${getSaveaPhoneTel('FR', 'electricity', 'LP')}`)}
              size="lg"
              className="bg-white text-sage-700 hover:bg-sage-50 font-bold px-4 lg:px-8 py-3 text-sm lg:text-base"
            >
              <Phone className="w-4 h-4 lg:w-5 lg:h-5 mr-2" />
              <span className="hidden sm:inline">Experts SAVEA</span>
              <span className="sm:hidden">{saveaPhone}</span>
            </Button>
            <Button 
              asChild
              variant="outline"
              size="lg"
              className="border-2 border-white text-white hover:bg-white hover:text-sage-700 font-bold px-4 lg:px-8 py-3 text-sm lg:text-base"
            >
              <Link to="/fr/modeles-emails">
                <span className="hidden sm:inline">📧 Templates de Négociation</span>
                <span className="sm:hidden">📧 Templates</span>
              </Link>
            </Button>
          </div>
          <p className="text-sage-200 text-sm">
            💡 L'argent économisé, ce n'est pas de l'argent perdu : c'est du pouvoir d'achat retrouvé, 
            pour vos projets, vos proches et vos plaisirs.
          </p>
        </div>
      </section>

      {/* CTA Final vers Produits Testés */}
      <section className="py-12 lg:py-16 bg-gradient-to-r from-amber-50 to-yellow-50 border-t-4 border-amber-300">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8 lg:mb-12">
            <h2 className="text-2xl lg:text-4xl font-bold text-amber-900 mb-3 lg:mb-4">🛒 Produits Testés et Recommandés SAVEA</h2>
            <p className="text-base lg:text-xl text-amber-700 mb-6 lg:mb-8">
              Découvrez notre sélection complète de produits économiques testés et validés par nos équipes
            </p>
            <Link
              to="/fr/produits-testes"
              className="inline-flex items-center gap-2 lg:gap-3 bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white font-bold px-6 py-3 lg:px-8 lg:py-4 rounded-xl transition-all shadow-lg hover:shadow-xl text-sm lg:text-base"
            >
              <ShoppingCart className="w-5 h-5 lg:w-6 lg:h-6" />
              <span>Voir tous les Produits</span>
              <ExternalLink className="w-4 h-4 lg:w-5 lg:h-5" />
            </Link>
          </div>

          {/* CTA Experts SAVEA */}
          <Card className="bg-gradient-to-r from-sage-600 to-sage-700 border-0 text-white">
            <CardContent className="p-6 lg:p-8 text-center">
              <h3 className="font-bold text-xl lg:text-2xl mb-3 lg:mb-4">💡 Besoin d'aide pour optimiser vos dépenses ?</h3>
              <p className="text-sage-100 mb-4 lg:mb-6 text-sm lg:text-base">
                Nos experts SAVEA vous accompagnent gratuitement pour mettre en pratique ces conseils
              </p>
              <Button 
                onClick={() => window.open(`tel:${getSaveaPhoneTel('FR', 'electricity', 'LP')}`)}
                size="lg"
                className="w-full lg:w-auto bg-white text-sage-700 hover:bg-sage-50 font-bold px-6 lg:px-8 py-3 lg:py-4"
              >
                <Phone className="w-5 h-5 mr-2" />
                <span className="text-sm lg:text-base">Expert SAVEA</span>
              </Button>
              <div className="text-sage-200 text-xs lg:text-sm mt-3 lg:mt-4">Service gratuit - Lundi à Samedi 8h-21h</div>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};